import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from './Navigation';
import FolderImageLabelModal from './FolderImageLabelModal';
import FolderLabelingStatusDock, { type FolderLabelingBanner } from './FolderLabelingStatusDock';
import AddressPicker from './AddressPicker';
import { collectFilesFromDataTransfer } from '../utils/collectDroppedFiles';
import { classifyFiles } from '../utils/folderImportClassify';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/config';
import {
  getAnalysisDraft,
  setAnalysisDraft,
  clearAnalysisDraftForUser,
  cloneImageCollections,
  type AnalysisDraftSnapshot,
} from '../utils/analysisDraftCache';
import {
  saveWeatherGenState,
  loadWeatherGenState,
  clearWeatherGenState,
} from '../utils/indexedDB';
import './AnalysisForm.css';

const WEATHER_VERIFY_POLL_INTERVAL_MS = 2000;
/** Stop only if a running job shows no phase/update progress for this long. */
const WEATHER_VERIFY_POLL_IDLE_MS = 35 * 60 * 1000;
/** Hard cap so a lost job cannot poll forever. */
const WEATHER_VERIFY_POLL_ABSOLUTE_MS = 2 * 60 * 60 * 1000;
const WEATHER_VERIFY_POST_MS = 30_000; // enqueue-only POST on main API

const DEFAULT_WEATHER_NARRATIVE_TYPES = [
  'wind',
  'tropical',
  'flood',
  'lightning',
  'hail',
  'freeze',
  'tornado',
  'other',
] as const;

type WeatherSectionKey =
  | 'loss_date_summary'
  | 'hourly_wind_detail'
  | 'hourly_precip_detail'
  | 'multi_day_overview'
  | 'precipitation_events'
  | 'freeze_events'
  | 'wind_events_table'
  | 'storm_event_narratives'
  | 'show_radius_columns'
  | 'station_list';

const WEATHER_REPORT_SECTION_GROUPS: ReadonlyArray<{
  id: string;
  title: string;
  hint?: string;
  sections: ReadonlyArray<{
    key: WeatherSectionKey;
    label: string;
    hint?: string;
  }>;
}> = [
  {
    id: 'loss_window',
    title: 'Loss window',
    sections: [
      {
        key: 'loss_date_summary',
        label: 'Loss date summary',
      },
      {
        key: 'hourly_wind_detail',
        label: 'Hourly wind detail',
      },
      {
        key: 'hourly_precip_detail',
        label: 'Hourly precip detail',
      },
    ],
  },
  {
    id: 'daily_history',
    title: 'Daily history',
    hint: 'Station + model merge over ~5 years',
    sections: [
      {
        key: 'multi_day_overview',
        label: 'Multi-day overview',
        hint: 'Days around the loss date',
      },
      {
        key: 'wind_events_table',
        label: 'Wind events',
      },
      {
        key: 'precipitation_events',
        label: 'Precipitation events',
      },
      {
        key: 'freeze_events',
        label: 'Freeze events',
      },
    ],
  },
  {
    id: 'storm',
    title: 'Storm narratives',
    sections: [
      {
        key: 'storm_event_narratives',
        label: 'Storm event narratives',
      },
    ],
  },
  {
    id: 'display',
    title: 'Display',
    sections: [
      {
        key: 'show_radius_columns',
        label: 'Radius columns',
        hint: '1 / 3 / 5 mile gust columns',
      },
      {
        key: 'station_list',
        label: 'Station list',
        hint: 'Nearby stations used in the report',
      },
    ],
  },
];

type WeatherMultiDayDays = 1 | 3 | 5;

const DEFAULT_WEATHER_MULTI_DAY_DAYS: WeatherMultiDayDays = 1;

const DEFAULT_WEATHER_SECTION_FLAGS: Record<WeatherSectionKey, boolean> = {
  loss_date_summary: false,
  hourly_wind_detail: false,
  hourly_precip_detail: false,
  multi_day_overview: false,
  precipitation_events: false,
  freeze_events: false,
  wind_events_table: false,
  storm_event_narratives: false,
  show_radius_columns: false,
  station_list: false,
};

type WeatherGenFormState = {
  address: string;
  date_of_loss: string;
  range_start: string;
  range_end: string;
  threshold_mph: string;
  rain_mm: string;
  multi_day_days: WeatherMultiDayDays;
} & Record<WeatherSectionKey, boolean>;

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places-script';

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/** Human-readable duration for automatic labeling (wall clock across all batches). */
function formatAutomaticLabelingDuration(totalMs: number): string {
  const sec = totalMs / 1000;
  if (sec < 60) return `${sec.toFixed(1)} s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  if (s === 60) return `${m + 1} min`;
  return s > 0 ? `${m} min ${s} s` : `${m} min`;
}

function formatLossDateDisplay(iso: string): string | null {
  const trimmed = iso.trim();
  if (!trimmed) return null;
  const d = new Date(`${trimmed}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const computeTightSvgViewBox = (rawSvg: string): string => {
  if (!rawSvg) return rawSvg;

  let cleaned = rawSvg
    .replace(/="NaN"/gi, '="0"')
    .replace(/="undefined"/gi, '="0"')
    .replace(/translate\s*\(\s*NaN\s*,\s*NaN\s*\)/gi, 'translate(0,0)')
    .replace(/scale\s*\([^)]*NaN[^)]*\)/gi, 'scale(1)');

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleaned, 'image/svg+xml');
    const svgEl = doc.documentElement;
    if (!svgEl || svgEl.tagName.toLowerCase() !== 'svg') return cleaned;

    svgEl.removeAttribute('width');
    svgEl.removeAttribute('height');

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    container.style.visibility = 'hidden';
    container.style.width = '2000px';
    container.style.height = '2000px';
    container.appendChild(doc.documentElement.cloneNode(true));
    document.body.appendChild(container);

    const tempSvg = container.querySelector('svg');
    if (tempSvg) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      const elements = tempSvg.querySelectorAll('path, line, polyline, polygon, circle, ellipse, text, rect, g');
      elements.forEach((node) => {
        try {
          const el = node as SVGGraphicsElement;
          const b = el.getBBox();
          if (b && b.width > 0 && b.height > 0 && isFinite(b.x) && isFinite(b.y) && isFinite(b.width) && isFinite(b.height)) {
            // Ignore gigantic outer background rects / frames (> 2500px width/height)
            if (node.tagName.toLowerCase() === 'rect' && (b.width > 2500 || b.height > 2500)) return;

            // Ignore dark layout sidebars / viewports with extreme aspect ratios
            const fill = (el.getAttribute('fill') || '').toLowerCase();
            const isDark = fill === 'black' || fill === '#000' || fill === '#000000';
            if (isDark && (b.height > 3 * b.width && b.height > 150)) return;

            minX = Math.min(minX, b.x);
            minY = Math.min(minY, b.y);
            maxX = Math.max(maxX, b.x + b.width);
            maxY = Math.max(maxY, b.y + b.height);
          }
        } catch {}
      });

      if (!isFinite(minX) || !isFinite(minY) || minX >= maxX || minY >= maxY) {
        try {
          const b = tempSvg.getBBox();
          if (b && b.width > 0 && b.height > 0 && isFinite(b.x) && isFinite(b.y)) {
            minX = b.x;
            minY = b.y;
            maxX = b.x + b.width;
            maxY = b.y + b.height;
          }
        } catch {}
      }

      if (isFinite(minX) && isFinite(minY) && maxX > minX && maxY > minY) {
        const w = maxX - minX;
        const h = maxY - minY;
        const padX = Math.max(w * 0.05, 10);
        const padY = Math.max(h * 0.05, 10);

        const vbX = (minX - padX).toFixed(2);
        const vbY = (minY - padY).toFixed(2);
        const vbW = (w + padX * 2).toFixed(2);
        const vbH = (h + padY * 2).toFixed(2);

        svgEl.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);
        svgEl.setAttribute('width', '100%');
        svgEl.setAttribute('height', '100%');
        svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      }
    }

    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }

    return new XMLSerializer().serializeToString(doc);
  } catch (err) {
    console.warn('computeTightSvgViewBox notice:', err);
    return cleaned;
  }
};

const AnalysisForm: React.FC = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const hasPlacesApiKey = Boolean(process.env.REACT_APP_GOOGLE_PLACES_API_KEY);
  const initialDraft = user?.id ? getAnalysisDraft(user.id) : null;

  const [formData, setFormData] = useState(() => {
    const defaults = {
      case_number: '',
      policy_number: '',
      project_number: '',
      address: '',
      date_start: '',
      date_end: '',
      engineer_notes: '',
      structure_type: 'single',
      total_floors: 1,
    };
    return initialDraft ? { ...defaults, ...initialDraft.formData } : defaults;
  });

  const [currentArea, setCurrentArea] = useState<string>(() => initialDraft?.currentArea ?? 'exterior');
  const [imageCollections, setImageCollections] = useState<{[key: string]: {files: File[], descriptions: string[]}}>(() =>
    initialDraft ? cloneImageCollections(initialDraft.imageCollections) : {}
  );
  const [selectedAudio, setSelectedAudio] = useState<File | null>(() => initialDraft?.selectedAudio ?? null);
  const [selectedWindReport, setSelectedWindReport] = useState<File | null>(() => initialDraft?.selectedWindReport ?? null);
  const [windReportExtraction, setWindReportExtraction] = useState<any>(() => initialDraft?.windReportExtraction ?? null);
  const [isWindReportModalOpen, setIsWindReportModalOpen] = useState(false);
  const [isExtractingWindReport, setIsExtractingWindReport] = useState(false);
  const [isWeatherGenModalOpen, setIsWeatherGenModalOpen] = useState(false);
  const [weatherGenLoading, setWeatherGenLoading] = useState(false);
  const [weatherGenPdfLoading, setWeatherGenPdfLoading] = useState(false);
  const [weatherGenErr, setWeatherGenErr] = useState('');
  const [weatherGenResultText, setWeatherGenResultText] = useState<string | null>(() =>
    initialDraft?.weatherGenResultText ?? null
  );
  // Structured report payload (mirrors weatherGenResultText) used to render a
  // branded, styled PDF via /api/weather-verification/report-pdf.
  const [weatherGenResultJson, setWeatherGenResultJson] = useState<Record<string, unknown> | null>(
    () => initialDraft?.weatherGenResultJson ?? null
  );
  const [weatherGenStats, setWeatherGenStats] = useState<{
    stations: number;
    isd_stations: number;
    wind_events: number;
    storm_events: number;
  } | null>(() => initialDraft?.weatherGenStats ?? null);
  const [weatherGenForm, setWeatherGenForm] = useState<WeatherGenFormState>({
    address: '',
    date_of_loss: '',
    range_start: '',
    range_end: '',
    threshold_mph: '58',
    rain_mm: '25.4',
    multi_day_days: DEFAULT_WEATHER_MULTI_DAY_DAYS,
    ...DEFAULT_WEATHER_SECTION_FLAGS,
  });
  const [weatherGenPreviewOpen, setWeatherGenPreviewOpen] = useState(false);
  const [svgData, setSvgData] = useState(() => initialDraft?.svgData ?? '');
  const [uploadedSvgFile, setUploadedSvgFile] = useState<File | null>(() => initialDraft?.uploadedSvgFile ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [results, setResults] = useState<any>(null);
  const [status, setStatus] = useState<string>('');
  const [savedSvgs, setSavedSvgs] = useState<Array<{name: string, data: string, preview: string}>>(() =>
    initialDraft ? initialDraft.savedSvgs.map((s) => ({ ...s })) : []
  );
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageForPreview, setSelectedImageForPreview] = useState<{file: File, area: string, index: number} | null>(null);
  const [isSvgModalOpen, setIsSvgModalOpen] = useState(false);
  const [selectedSvgForPreview, setSelectedSvgForPreview] = useState<{name: string, data: string, preview: string} | null>(null);
  const [editingSvg, setEditingSvg] = useState<{name: string, data: string, preview: string} | null>(null);
  const [isEditorFullScreen, setIsEditorFullScreen] = useState(false);
  const [editorIframeSrc, setEditorIframeSrc] = useState<string | null>(null);
  const [isCadViewerOpen, setIsCadViewerOpen] = useState(false);
  const [isCadViewerFullScreen, setIsCadViewerFullScreen] = useState(false);
  const [cadViewerIframeSrc, setCadViewerIframeSrc] = useState<string | null>(null);
  const [pendingCadFile, setPendingCadFile] = useState<File | null>(null);
  const cadViewerIframeRef = useRef<HTMLIFrameElement>(null);
  const [downloadReportLoading, setDownloadReportLoading] = useState<string | null>(null);
  const [analysisStartedCaseNumber, setAnalysisStartedCaseNumber] = useState<string | null>(
    () => initialDraft?.analysisStartedCaseNumber ?? null
  );

  const [isWeatherGenStateLoaded, setIsWeatherGenStateLoaded] = useState(false);

  useEffect(() => {
    loadWeatherGenState().then((state) => {
      if (state) {
        setWeatherGenForm(prev => ({ ...prev, ...state }));
      }
      setIsWeatherGenStateLoaded(true);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (isWeatherGenStateLoaded) {
      saveWeatherGenState(weatherGenForm).catch(console.error);
    }
  }, [weatherGenForm, isWeatherGenStateLoaded]);

  const skipPersistDraftRef = useRef(false);
  const analysisDraftSnapshotRef = useRef<AnalysisDraftSnapshot | null>(null);
  const draftOwnerUserIdRef = useRef<string | null>(null);
  draftOwnerUserIdRef.current = user?.id ?? null;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const folderPickerRef = useRef<HTMLInputElement>(null);
  // Ensure non-standard webkitdirectory/directory attributes are set on the DOM
  // (React does not reliably pass unknown attributes through the spread props).
  useEffect(() => {
    const el = folderPickerRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
  }, []);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const addressAutocompleteServiceRef = useRef<any>(null);
  const addressSuggestionsRef = useRef<HTMLDivElement>(null);
  const addressInputDebounceRef = useRef<number | null>(null);
  const wvAddressInputRef = useRef<HTMLInputElement>(null);
  const wvAddressSuggestionsRef = useRef<HTMLDivElement>(null);
  const wvAddressInputDebounceRef = useRef<number | null>(null);
  /** Avoids a second /report-pdf round-trip when user downloads then attaches the same report.
   *  Keyed on the JSON payload (preferred) or the report text (fallback) so callers
   *  that only have the text still benefit from the cache. */
  const weatherGenPdfCacheRef = useRef<
    | { reportJson: Record<string, unknown> | null; reportText: string; blob: Blob; filename: string }
    | null
  >(null);
  const [isPlacesReady, setIsPlacesReady] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<Array<{ place_id: string; description: string }>>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isAddressLookupLoading, setIsAddressLookupLoading] = useState(false);
  const [addressLookupMessage, setAddressLookupMessage] = useState<string>('');
  const [wvAddressSuggestions, setWvAddressSuggestions] = useState<Array<{ place_id: string; description: string }>>([]);
  const [showWvAddressSuggestions, setShowWvAddressSuggestions] = useState(false);
  const [isWvAddressLookupLoading, setIsWvAddressLookupLoading] = useState(false);
  const [wvAddressLookupMessage, setWvAddressLookupMessage] = useState<string>('');
  const [showNotes, setShowNotes] = useState(() => initialDraft?.showNotes ?? false);
  const [showAudioSection, setShowAudioSection] = useState(() => initialDraft?.showAudioSection ?? false);
  const [showWindSection, setShowWindSection] = useState(() => initialDraft?.showWindSection ?? false);
  const [showSvgSection, setShowSvgSection] = useState(() => initialDraft?.showSvgSection ?? false);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(() => initialDraft?.currentStep ?? 1);
  const [folderAutoLabelBusy, setFolderAutoLabelBusy] = useState(false);
  const [folderImportFeedback, setFolderImportFeedback] = useState('');
  const [labelingBanner, setLabelingBanner] = useState<FolderLabelingBanner | null>(null);
  const [folderDropActive, setFolderDropActive] = useState(false);
  const [imageLabelChoiceOpen, setImageLabelChoiceOpen] = useState(false);
  const [stagedImagesForLabeling, setStagedImagesForLabeling] = useState<File[]>([]);
  const [folderImageLabelOpen, setFolderImageLabelOpen] = useState(false);
  const [folderImportBusy, setFolderImportBusy] = useState(false);

  analysisDraftSnapshotRef.current = {
    formData,
    currentArea,
    imageCollections,
    selectedAudio,
    selectedWindReport,
    windReportExtraction,
    weatherGenResultText,
    weatherGenResultJson,
    weatherGenStats,
    svgData,
    uploadedSvgFile,
    savedSvgs,
    currentStep,
    showNotes,
    showAudioSection,
    showWindSection,
    showSvgSection,
    analysisStartedCaseNumber,
  };

  useEffect(() => {
    return () => {
      if (skipPersistDraftRef.current) {
        skipPersistDraftRef.current = false;
        return;
      }
      const uid = draftOwnerUserIdRef.current;
      if (!uid) return;
      const s = analysisDraftSnapshotRef.current;
      if (!s) return;
      setAnalysisDraft(uid, {
        formData: { ...s.formData },
        currentArea: s.currentArea,
        imageCollections: cloneImageCollections(s.imageCollections),
        selectedAudio: s.selectedAudio,
        selectedWindReport: s.selectedWindReport,
        windReportExtraction: s.windReportExtraction,
        weatherGenResultText: s.weatherGenResultText,
        weatherGenResultJson: s.weatherGenResultJson,
        weatherGenStats: s.weatherGenStats,
        svgData: s.svgData,
        uploadedSvgFile: s.uploadedSvgFile,
        savedSvgs: s.savedSvgs.map((x) => ({ ...x })),
        currentStep: s.currentStep,
        showNotes: s.showNotes,
        showAudioSection: s.showAudioSection,
        showWindSection: s.showWindSection,
        showSvgSection: s.showSvgSection,
        analysisStartedCaseNumber: s.analysisStartedCaseNumber,
      });
    };
  }, []);

  useEffect(() => {
    if (!labelingBanner || labelingBanner.kind !== 'success') return;
    const t = window.setTimeout(() => setLabelingBanner(null), 12000);
    return () => window.clearTimeout(t);
  }, [labelingBanner]);

  const handleDownloadReport = async (url: string, label: string) => {
    setDownloadReportLoading(label);
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { ...getAuthHeaders() },
      });
      if (response.status === 401) {
        setError('Session expired. Please log in again.');
        return;
      }
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Download failed');
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition?.match(/filename[*]?=(?:UTF-8'')?["']?([^"'\s]+)["']?/i);
      const filename = match?.[1] || `${results?.case_number || 'report'}_report.docx`;
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(objectUrl);
      a.remove();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadReportLoading(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'address') {
      setShowAddressSuggestions(true);
      setAddressLookupMessage('');
      if (!value.trim()) {
        setAddressSuggestions([]);
      }
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAreaChange = (area: string) => {
    setCurrentArea(area);
  };

  const handleAddImagesToArea = (files: File[]) => {
    const newFiles = Array.from(files);
    const newDescriptions = new Array(newFiles.length).fill('');
    
    setImageCollections(prev => ({
      ...prev,
      [currentArea]: {
        files: [...(prev[currentArea]?.files || []), ...newFiles],
        descriptions: [...(prev[currentArea]?.descriptions || []), ...newDescriptions]
      }
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleAddImagesToArea(files);
    }
  };

  const handleDescriptionChange = (area: string, index: number, value: string) => {
    setImageCollections(prev => ({
      ...prev,
      [area]: {
        ...prev[area],
        descriptions: prev[area].descriptions.map((desc, i) => i === index ? value : desc)
      }
    }));
  };

  const handleRemoveImage = (area: string, index: number) => {
    setImageCollections(prev => ({
      ...prev,
      [area]: {
        files: prev[area].files.filter((_, i) => i !== index),
        descriptions: prev[area].descriptions.filter((_, i) => i !== index)
      }
    }));
  };

  const handleImageClick = (file: File, area: string, index: number) => {
    setSelectedImageForPreview({ file, area, index });
    setIsImageModalOpen(true);
    
    // Scroll to the modal body
    setTimeout(() => {
      const modalBody = document.querySelector('.image-modal-body');
      if (modalBody) {
        modalBody.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImageForPreview(null);
  };

  const handleSvgPreview = (svg: {name: string, data: string, preview: string}) => {
    setSelectedSvgForPreview(svg);
    setIsSvgModalOpen(true);
    
    // Scroll to the SVG modal body
    setTimeout(() => {
      const modalBody = document.querySelector('.svg-modal-body');
      if (modalBody) {
        modalBody.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleCloseSvgModal = () => {
    setIsSvgModalOpen(false);
    setSelectedSvgForPreview(null);
  };

  const handleEditSvg = (svg: {name: string, data: string, preview: string}) => {
    setEditingSvg(svg);
    setSvgData(svg.data);
    setIsEditorOpen(true);
    
    // Scroll to the editor after it opens
    setTimeout(() => {
      const editorContainer = document.querySelector('.svg-editor-container');
      if (editorContainer) {
        editorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedAudio(e.target.files[0]);
    }
  };

  const handleRemoveAudio = () => {
    setSelectedAudio(null);
    if (audioInputRef.current) {
      audioInputRef.current.value = '';
    }
  };

  const handleWindReportChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedWindReport(e.target.files[0]);
      // Clear previous extraction when new file is selected
      setWindReportExtraction(null);
    }
  };

  const handleExtractWindReport = async () => {
    if (!selectedWindReport) {
      alert('Please select a wind report PDF first');
      return;
    }

    setIsExtractingWindReport(true);
    setStatus('Extracting wind data from document...');

    try {
      const formData = new FormData();
      formData.append('wind_report', selectedWindReport);

      const response = await axios.post('/api/wind-report/extract', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders(),
        },
        withCredentials: true,
      });

      if (response.data.success) {
        setWindReportExtraction(response.data.data);
        setIsWindReportModalOpen(true);
        setStatus('Wind data extracted successfully!');
        console.log('Wind report extraction:', response.data.data);
      } else {
        setError(response.data.error || 'Failed to extract wind data');
        setStatus('');
      }
    } catch (error: any) {
      console.error('Wind report extraction error:', error);
      setError(error.response?.data?.error || 'Failed to extract wind data from document');
      setStatus('');
    } finally {
      setIsExtractingWindReport(false);
    }
  };

  const handleCloseWindReportModal = () => {
    setIsWindReportModalOpen(false);
  };

  const openWeatherGenModal = () => {
    const dateStart = (formData.date_start || '').trim();
    const dateEnd = (formData.date_end || '').trim();
    const dol = dateStart.slice(0, 10);

    // range_start: 5 years before date of loss (same as weather-apis static/app.js calcRangeStart)
    let rangeStart = '';
    if (dol) {
      try {
        const d = new Date(`${dol}T00:00:00`);
        if (!Number.isNaN(d.getTime())) {
          d.setFullYear(d.getFullYear() - 5);
          rangeStart = d.toISOString().slice(0, 10);
        }
      } catch {
        rangeStart = '';
      }
    }

    // range_end: case incident end if set; else loss + 2 days (calcRangeEnd in original app)
    let rangeEnd = '';
    if (dateEnd) {
      rangeEnd = dateEnd.slice(0, 10);
    } else if (dol) {
      try {
        const d = new Date(`${dol}T00:00:00`);
        if (!Number.isNaN(d.getTime())) {
          d.setDate(d.getDate() + 2);
          rangeEnd = d.toISOString().slice(0, 10);
        }
      } catch {
        rangeEnd = '';
      }
    }

    setWeatherGenForm((prev) => ({
      ...prev,
      address: formData.address.trim() || prev.address,
      date_of_loss: dol || prev.date_of_loss,
      range_start: rangeStart || prev.range_start,
      range_end: rangeEnd || prev.range_end,
    }));
    // Preserve any previously generated report text/stats so reopening the
    // modal (or returning to the analysis page) does not discard the report.
    weatherGenPdfCacheRef.current = null;
    setWeatherGenErr('');
    setIsWeatherGenModalOpen(true);
  };

  const handleCloseWeatherGenModal = () => {
    setIsWeatherGenModalOpen(false);
    setWeatherGenErr('');
    weatherGenPdfCacheRef.current = null;
  };

  const weatherVerificationPdfFilename = () => {
    const safeCase = (formData.case_number || 'report').replace(/[^\w-]+/g, '_').slice(0, 80);
    return `weather_verification_${safeCase}.pdf`;
  };

  const fetchWeatherVerificationPdfBlob = async (
    reportText: string,
    reportJson?: Record<string, unknown> | null,
  ) => {
    const filename = weatherVerificationPdfFilename();
    const hit = weatherGenPdfCacheRef.current;
    if (
      hit &&
      hit.reportText === reportText &&
      ((reportJson && hit.reportJson === reportJson) || (!reportJson && !hit.reportJson))
    ) {
      return { blob: hit.blob, filename: hit.filename };
    }
    const body: Record<string, unknown> = { filename };
    if (reportJson && typeof reportJson === 'object') {
      body.report_json = reportJson;
    } else {
      body.report_text = reportText;
    }
    const response = await fetch(`${API_URL}/api/weather-verification/report-pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    });
    if (response.status === 401) {
      throw new Error('Session expired. Please log in again.');
    }
    if (!response.ok) {
      const j = await response.json().catch(() => ({}));
      throw new Error((j as { error?: string }).error || 'PDF request failed');
    }
    const blob = await response.blob();
    weatherGenPdfCacheRef.current = {
      reportJson: reportJson ?? null,
      reportText,
      blob,
      filename,
    };
    return { blob, filename };
  };

  const buildWeatherGenRequestBody = () => {
    const threshold = parseFloat(weatherGenForm.threshold_mph);
    const rain = parseFloat(weatherGenForm.rain_mm);
    const policy = (formData.policy_number || '').trim();
    const project = (formData.project_number || '').trim();
    const insuredParts: string[] = [];
    if (policy) insuredParts.push(`Policy ${policy}`);
    if (project) insuredParts.push(`Project ${project}`);
    const insuredName = insuredParts.join(' · ');
    const caseNo = (formData.case_number || '').trim();
    const claimRef = caseNo || policy;
    return {
      address: weatherGenForm.address.trim(),
      date_of_loss: weatherGenForm.date_of_loss.trim() || '2024-10-10',
      claim_ref: claimRef || undefined,
      insured_name: insuredName || undefined,
      range_start: weatherGenForm.range_start.trim() || undefined,
      range_end: weatherGenForm.range_end.trim() || undefined,
      threshold_mph: Number.isFinite(threshold) ? threshold : 58,
      rain_mm: Number.isFinite(rain) ? rain : 25.4,
      multi_day_days: weatherGenForm.multi_day_days,
      narrative_types: [...DEFAULT_WEATHER_NARRATIVE_TYPES],
      loss_date_summary: weatherGenForm.loss_date_summary,
      hourly_wind_detail: weatherGenForm.hourly_wind_detail,
      hourly_precip_detail: weatherGenForm.hourly_precip_detail,
      multi_day_overview: weatherGenForm.multi_day_overview,
      precipitation_events: weatherGenForm.precipitation_events,
      freeze_events: weatherGenForm.freeze_events,
      wind_events_table: weatherGenForm.wind_events_table,
      storm_event_narratives: weatherGenForm.storm_event_narratives,
      show_radius_columns: weatherGenForm.show_radius_columns,
      station_list: weatherGenForm.station_list,
    };
  };

  const pollWeatherVerificationJob = async (jobId: string) => {
    const startedAt = Date.now();
    let lastProgressAt = Date.now();
    let lastPhase = '';
    let lastUpdatedAt = '';
    let authRetryCount = 0;
    while (Date.now() - startedAt < WEATHER_VERIFY_POLL_ABSOLUTE_MS) {
      let statusRes;
      try {
        // Request include_json=true so the structured report payload is returned
        // alongside report_text; the frontend forwards it to /report-pdf to
        // render a branded, styled PDF (Forensic AI cover + styled tables).
        statusRes = await axios.get(
          `${API_URL}/api/weather-verification/generate/${jobId}?include_json=true`,
          {
            withCredentials: true,
            headers: { ...getAuthHeaders() },
            timeout: 60000,
          },
        );
        authRetryCount = 0;
      } catch (pollErr: unknown) {
        const ax = pollErr as {
          response?: { status?: number; data?: { error?: string } };
        };
        if (
          ax.response?.status === 404 &&
          Date.now() - startedAt < 30_000
        ) {
          setStatus('Waiting for weather report job to register…');
          await new Promise((r) => setTimeout(r, WEATHER_VERIFY_POLL_INTERVAL_MS));
          continue;
        }
        if (ax.response?.status === 401 && authRetryCount < 3) {
          authRetryCount += 1;
          setStatus('Auth check slow — retrying poll…');
          await new Promise((r) => setTimeout(r, 2500));
          continue;
        }
        throw pollErr;
      }
      const d = statusRes.data as {
        success?: boolean;
        status?: string;
        phase?: string;
        updated_at?: string;
        report_text?: string;
        report_json?: Record<string, unknown>;
        stations?: number;
        isd_stations?: number;
        wind_events?: number;
        storm_events?: number;
        error?: string;
      };
      const phase = typeof d.phase === 'string' ? d.phase.trim() : '';
      const updatedAt = typeof d.updated_at === 'string' ? d.updated_at : '';
      if (phase && phase !== lastPhase) {
        lastPhase = phase;
        lastProgressAt = Date.now();
        setStatus(phase);
      } else if (updatedAt && updatedAt !== lastUpdatedAt) {
        lastUpdatedAt = updatedAt;
        lastProgressAt = Date.now();
      }
      if (d.status === 'queued') {
        setStatus(phase || 'Weather report queued…');
      } else if (d.status === 'running' && !phase) {
        setStatus('Generating weather verification report…');
      }
      if (
        d.status === 'running' &&
        Date.now() - lastProgressAt > WEATHER_VERIFY_POLL_IDLE_MS
      ) {
        throw new Error(
          'Weather report stopped making progress. Check Cloud Run logs — the job may still be running.',
        );
      }
      if (d.status === 'completed') {
        if (typeof d.report_text === 'string') {
          return d;
        }
        throw new Error(
          'Weather report completed on the server but report_text was missing. Check Cloud Run logs.',
        );
      }
      if (d.status === 'failed') {
        throw new Error(d.error || 'Weather report generation failed');
      }
      await new Promise((r) => setTimeout(r, WEATHER_VERIFY_POLL_INTERVAL_MS));
    }
    throw new Error(
      'Weather report generation exceeded the maximum wait time. Check Cloud Run logs — the job may still be running.',
    );
  };

  const handleWeatherGenSubmit = async () => {
    if (!weatherGenForm.address.trim()) {
      setWeatherGenErr('Property address is required.');
      return;
    }
    setWeatherGenLoading(true);
    setWeatherGenErr('');
    weatherGenPdfCacheRef.current = null;
    // Drop the previous report's structured payload so a stale JSON is never
    // sent to /report-pdf for a fresh generation (it is repopulated on success).
    setWeatherGenResultJson(null);
    setStatus('Generating weather verification report (fetching NOAA / Open-Meteo data)…');
    try {
      const body = buildWeatherGenRequestBody();
      const response = await axios.post(`${API_URL}/api/weather-verification/generate`, body, {
        headers: { ...getAuthHeaders() },
        withCredentials: true,
        timeout: WEATHER_VERIFY_POST_MS,
      });
      let result = response.data as {
        success?: boolean;
        job_id?: string;
        report_text?: string;
        report_json?: Record<string, unknown>;
        stations?: number;
        isd_stations?: number;
        wind_events?: number;
        storm_events?: number;
        error?: string;
      };
      if (response.status === 202 && result.job_id) {
        result = await pollWeatherVerificationJob(String(result.job_id));
      }
      if (result?.success && typeof result.report_text === 'string') {
        setWeatherGenResultText(result.report_text);
        setWeatherGenResultJson(
          result.report_json && typeof result.report_json === 'object'
            ? result.report_json
            : null,
        );
        setWeatherGenStats({
          stations: result.stations ?? 0,
          isd_stations: result.isd_stations ?? 0,
          wind_events: result.wind_events ?? 0,
          storm_events: result.storm_events ?? 0,
        });
        setStatus('Weather verification report generated.');
        clearWeatherGenState().catch(console.error);
      } else {
        setWeatherGenErr(result?.error || 'Generation failed');
        setStatus('');
      }
    } catch (err: unknown) {
      const ax = err as {
        message?: string;
        response?: { status?: number; data?: { error?: string } };
      };
      const msg =
        ax.response?.data?.error ||
        ax.message ||
        'Failed to generate weather report.';
      setWeatherGenErr(typeof msg === 'string' ? msg : 'Failed to generate weather report.');
      setStatus('');
    } finally {
      setWeatherGenLoading(false);
    }
  };

  const handleWeatherGenDownloadPdf = async () => {
    if (!weatherGenResultText) {
      setWeatherGenErr('Generate a report first.');
      return;
    }
    setWeatherGenPdfLoading(true);
    setWeatherGenErr('');
    try {
      const { blob, filename } = await fetchWeatherVerificationPdfBlob(
        weatherGenResultText,
        weatherGenResultJson,
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'PDF download failed';
      setWeatherGenErr(msg);
    } finally {
      setWeatherGenPdfLoading(false);
    }
  };

  const handleWeatherGenAttachPdf = async () => {
    if (!weatherGenResultText) {
      setWeatherGenErr('Generate a report first.');
      return;
    }
    setWeatherGenPdfLoading(true);
    setWeatherGenErr('');
    try {
      const { blob, filename } = await fetchWeatherVerificationPdfBlob(
        weatherGenResultText,
        weatherGenResultJson,
      );
      const file = new File([blob], filename, { type: 'application/pdf' });
      setSelectedWindReport(file);
      setWindReportExtraction(null);
      setIsWeatherGenModalOpen(false);
      setStatus('Generated weather report attached. You can run “Extract Wind Data” if needed.');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not attach PDF';
      setWeatherGenErr(msg);
    } finally {
      setWeatherGenPdfLoading(false);
    }
  };

  const ingestSvgFromFile = useCallback(
    async (
      file: File,
      opts?: { showAlerts?: boolean; useGlobalLoading?: boolean }
    ): Promise<'ok' | 'invalid' | 'fail'> => {
      const showAlerts = opts?.showAlerts ?? false;
      const useGlobalLoading = opts?.useGlobalLoading ?? true;
      if (!file.type.includes('svg') && !file.name.toLowerCase().endsWith('.svg')) {
        if (showAlerts) alert('Please select a valid SVG file.');
        return 'invalid';
      }
      if (file.size > 50 * 1024 * 1024) {
        if (showAlerts) alert('File size must be less than 50MB.');
        return 'invalid';
      }
      setUploadedSvgFile(file);
      let svgContent: string;
      try {
        svgContent = await readFileAsText(file);
      } catch {
        if (showAlerts) alert('Error reading the SVG file. Please try again.');
        return 'fail';
      }
      setSvgData(svgContent);
      if (useGlobalLoading) setLoading(true);
      try {
        const response = await axios.post(
          `${API_URL}/save_svg`,
          { svg: svgContent },
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
          }
        );
        if (response.data.success) {
          const previewUrl = URL.createObjectURL(
            new Blob([svgContent], { type: 'image/svg+xml' })
          );
          const newSvg = {
            name: response.data.filename,
            data: svgContent,
            preview: previewUrl,
          };
          setSavedSvgs((prev) => [...prev, newSvg]);
          if (showAlerts) {
            alert(`SVG file "${file.name}" uploaded and saved as ${response.data.filename}!`);
          }
          return 'ok';
        }
        if (showAlerts) alert('Failed to save uploaded SVG file.');
        return 'fail';
      } catch (error) {
        console.error('Error saving uploaded SVG:', error);
        if (showAlerts) alert('Error saving uploaded SVG file. Please try again.');
        return 'fail';
      } finally {
        if (useGlobalLoading) setLoading(false);
      }
    },
    []
  );

  const handleSvgFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      void ingestSvgFromFile(e.target.files[0], { showAlerts: true, useGlobalLoading: true });
    }
  };

  const handleOpenEditor = () => {
    setIsEditorOpen(true);
    // Scroll to the editor after it opens
    setTimeout(() => {
      const editorContainer = document.querySelector('.svg-editor-container');
      if (editorContainer) {
        editorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100); // Small delay to ensure editor is rendered
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setEditingSvg(null);
    setIsEditorFullScreen(false);
    setEditorIframeSrc(null);
  };

  const toggleEditorFullScreen = () => {
    setIsEditorFullScreen((prev) => !prev);
  };

  // CAD Viewer handlers
  const handleOpenCadViewer = () => {
    setIsCadViewerOpen(true);
  };

  const handleCadFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPendingCadFile(file);
      setIsCadViewerOpen(true);
    }
  };

  const handleCloseCadViewer = () => {
    setIsCadViewerOpen(false);
    setIsCadViewerFullScreen(false);
    setCadViewerIframeSrc(null);
    setPendingCadFile(null);
  };

  const toggleCadViewerFullScreen = () => {
    setIsCadViewerFullScreen((prev) => !prev);
  };

  const handleRequestSvgExport = () => {
    if (cadViewerIframeRef.current?.contentWindow) {
      cadViewerIframeRef.current.contentWindow.postMessage({ type: 'request-svg-export' }, '*');
    }
  };

  useEffect(() => {
    if (!isCadViewerOpen) return;
    const id = setTimeout(() => {
      setCadViewerIframeSrc(`${API_URL}/static/cadviewer/dist/index.html`);
    }, 80);
    return () => clearTimeout(id);
  }, [isCadViewerOpen]);

  useEffect(() => {
    if (!pendingCadFile || !cadViewerIframeSrc) return;
    const iframe = cadViewerIframeRef.current;
    if (!iframe) return;

    const handleIframeLoad = async () => {
      try {
        const buffer = await pendingCadFile.arrayBuffer();
        setTimeout(() => {
          iframe.contentWindow?.postMessage(
            { type: 'open-file', fileBuffer: buffer, fileName: pendingCadFile.name },
            '*'
          );
          setPendingCadFile(null);
        }, 400);
      } catch (err) {
        console.error('Failed to send CAD file buffer:', err);
      }
    };

    iframe.addEventListener('load', handleIframeLoad, { once: true });
    return () => iframe.removeEventListener('load', handleIframeLoad);
  }, [pendingCadFile, cadViewerIframeSrc]);

  useEffect(() => {
    const handleCadViewerMessage = async (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data.type !== 'string') return;

      if (data.type === 'svg-exported') {
        let svgContent = data.svgData as string;
        const fileName = (data.fileName as string) || 'cad-export.svg';

        if (!svgContent) return;

        // Auto-fit SVG viewBox to actual graphics elements
        svgContent = computeTightSvgViewBox(svgContent);

        const previewUrl = URL.createObjectURL(new Blob([svgContent], { type: 'image/svg+xml' }));
        const newSvg = {
          name: fileName,
          data: svgContent,
          preview: previewUrl,
        };
        setSavedSvgs((prev) => [...prev, newSvg]);
        setSvgData(svgContent);
        setShowSvgSection(true);

        try {
          const response = await axios.post(
            `${API_URL}/save_svg`,
            { svg: svgContent },
            {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true,
            }
          );
          if (response.data.success) {
            setSavedSvgs((prev) =>
              prev.map((s) =>
                s === newSvg ? { ...s, name: response.data.filename } : s
              )
            );
          }
        } catch (err) {
          console.error('Failed to save exported CAD SVG to server:', err);
        }
      }
    };

    window.addEventListener('message', handleCadViewerMessage);
    return () => window.removeEventListener('message', handleCadViewerMessage);
  }, []);

  // Defer iframe src so container has layout on first open (avoids blank/minimal editor)
  useEffect(() => {
    if (!isEditorOpen) return;
    const id = setTimeout(() => {
      setEditorIframeSrc(
        `${API_URL}/static/svgedit/dist/editor/index.html${editingSvg ? `?svg=${encodeURIComponent(editingSvg.data)}` : ''}`
      );
    }, 80);
    return () => clearTimeout(id);
  }, [isEditorOpen, editingSvg]);

  // Listen for postMessage from CAD viewer iframe (SVG export results)
  useEffect(() => {
    const handleCadViewerMessage = async (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data.type !== 'string') return;

      if (data.type === 'svg-exported') {
        let svgContent = data.svgData as string;
        const fileName = (data.fileName as string) || 'cad-export.svg';

        if (!svgContent) return;

        // Clean & Auto-fit SVG for display
        try {
          // 1. Replace broken NaN / undefined attribute values
          svgContent = svgContent
            .replace(/="NaN"/gi, '="0"')
            .replace(/="undefined"/gi, '="0"')
            .replace(/translate\s*\(\s*NaN\s*,\s*NaN\s*\)/gi, 'translate(0,0)')
            .replace(/translate\s*\(\s*NaN\s*,\s*([\d.-]+)\s*\)/gi, 'translate(0,$1)')
            .replace(/translate\s*\(\s*([\d.-]+)\s*,\s*NaN\s*\)/gi, 'translate($1,0)')
            .replace(/scale\s*\([^)]*NaN[^)]*\)/gi, 'scale(1)')
            .replace(/matrix\s*\(([^)]+)\)/gi, (match, inner) => {
              if (/NaN|undefined/i.test(inner)) {
                return 'matrix(1,0,0,1,0,0)';
              }
              return match;
            });

          // 2. Neutralize extreme transform matrices from corrupted DWG block references
          svgContent = svgContent.replace(/matrix\s*\(([^)]+)\)/gi, (match, inner) => {
            try {
              const vals = inner.split(',').map((s: string) => parseFloat(s.trim()));
              if (vals.length === 6) {
                const [a, , , d, e, f] = vals;
                if (Math.abs(a) > 100 || Math.abs(d) > 100 || Math.abs(e) > 1e7 || Math.abs(f) > 1e7) {
                  return 'matrix(1,0,0,1,0,0)';
                }
              }
            } catch {}
            return match;
          });

          // 3. Remove broken rects with scientific notation coordinates
          svgContent = svgContent.replace(/<rect\s[^>]*(?:x|y|width|height)\s*=\s*"[^"]*e[+-]?\d+[^"]*"[^>]*\/?\s*>/gi, '');

          // 4. Sanitize any remaining scientific notation numbers
          svgContent = svgContent.replace(/="([^"]*)"/g, (match, val) => {
            if (/\d+e[+-]?\d+/i.test(val)) {
              const sanitized = val.replace(/-?\d+(?:\.\d+)?e[+-]?\d+/gi, (num: string) => {
                const n = parseFloat(num);
                return isFinite(n) ? n.toFixed(6) : '0';
              });
              return `="${sanitized}"`;
            }
            return match;
          });

          // 5. Calculate true bounding box by scanning sanitized path coordinates
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          const pathRegex = /d="([^"]+)"/g;
          let m;
          while ((m = pathRegex.exec(svgContent)) !== null) {
            const coords = m[1].match(/-?\d+\.\d+|-?\d+/g);
            if (coords) {
              for (let i = 0; i < coords.length - 1; i += 2) {
                const x = parseFloat(coords[i]);
                const y = parseFloat(coords[i+1]);
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }

          let vbMinX = 0, vbMinY = 0, vbW = 800, vbH = 600;
          if (minX !== Infinity) {
            // The CAD export wraps everything in <g transform="matrix(1,0,0,-1,0,0)">
            // which means visual_y = -y. True minY is -maxY, true maxY is -minY.
            const trueMinY = -maxY;
            const trueMaxY = -minY;
            const padX = (maxX - minX) * 0.05;
            const padY = (trueMaxY - trueMinY) * 0.05;
            
            vbMinX = minX - padX;
            vbMinY = trueMinY - padY;
            vbW = (maxX - minX) + padX * 2;
            vbH = (trueMaxY - trueMinY) + padY * 2;
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(svgContent, 'image/svg+xml');
          const svgEl = doc.documentElement;

          if (svgEl && svgEl.tagName.toLowerCase() === 'svg') {
            // 6. Apply perfectly calculated bounding box
            svgEl.setAttribute('viewBox', `${vbMinX.toFixed(2)} ${vbMinY.toFixed(2)} ${vbW.toFixed(2)} ${vbH.toFixed(2)}`);
            svgEl.setAttribute('width', '100%');
            svgEl.setAttribute('height', '100%');
            svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');

            // 7. Insert dark background rect
            const bgRect = doc.createElementNS('http://www.w3.org/2000/svg', 'rect');
            bgRect.setAttribute('x', `${vbMinX}`);
            bgRect.setAttribute('y', `${vbMinY}`);
            bgRect.setAttribute('width', `${vbW}`);
            bgRect.setAttribute('height', `${vbH}`);
            bgRect.setAttribute('fill', '#1a1a2e');
            svgEl.insertBefore(bgRect, svgEl.firstChild);

            if (svgEl.getAttribute('x') === 'NaN') svgEl.setAttribute('x', '0');
            if (svgEl.getAttribute('y') === 'NaN') svgEl.setAttribute('y', '0');
            svgContent = new XMLSerializer().serializeToString(doc);
          }
        } catch (err) {
          console.warn('Failed to clean SVG attributes:', err);
        }

        // Create a fast, zero-copy Blob preview URL and add to savedSvgs directly
        const previewUrl = URL.createObjectURL(new Blob([svgContent], { type: 'image/svg+xml' }));
        const newSvg = {
          name: fileName,
          data: svgContent,
          preview: previewUrl,
        };
        setSavedSvgs((prev) => [...prev, newSvg]);
        setSvgData(svgContent);
        setShowSvgSection(true);

        try {
          const response = await axios.post(
            `${API_URL}/save_svg`,
            { svg: svgContent },
            {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true,
            }
          );
          if (response.data.success) {
            setSavedSvgs((prev) =>
              prev.map((s) =>
                s === newSvg ? { ...s, name: response.data.filename } : s
              )
            );
          }
        } catch (err) {
          console.error('Failed to save CAD-exported SVG to backend:', err);
        }
      }
    };

    window.addEventListener('message', handleCadViewerMessage);
    return () => window.removeEventListener('message', handleCadViewerMessage);
  }, []);

  // Load Google Maps Places script for address suggestions.
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
    if (!apiKey) {
      return;
    }

    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      if ((window as any).google?.maps?.places) {
        setIsPlacesReady(true);
      } else {
        existingScript.addEventListener('load', () => setIsPlacesReady(true), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsPlacesReady(true);
    script.onerror = () => {
      console.error('Failed to load Google Maps Places API script');
    };
    document.head.appendChild(script);
  }, []);

  // Initialize Google Places autocomplete service for custom-rendered suggestions.
  useEffect(() => {
    if (!isPlacesReady) {
      return;
    }
    if (!(window as any).google?.maps?.places) {
      return;
    }
    if (!addressAutocompleteServiceRef.current) {
      addressAutocompleteServiceRef.current = new (window as any).google.maps.places.AutocompleteService();
    }
  }, [isPlacesReady]);

  // Fetch address suggestions as user types (debounced).
  useEffect(() => {
    if (!isPlacesReady || !hasPlacesApiKey) {
      return;
    }
    const query = formData.address.trim();
    if (query.length < 3) {
      setAddressSuggestions([]);
      setAddressLookupMessage('');
      setIsAddressLookupLoading(false);
      return;
    }
    if (!addressAutocompleteServiceRef.current) {
      return;
    }

    if (addressInputDebounceRef.current) {
      window.clearTimeout(addressInputDebounceRef.current);
    }

    setIsAddressLookupLoading(true);
    setAddressLookupMessage('');
    addressInputDebounceRef.current = window.setTimeout(() => {
      const safetyTimeoutId = window.setTimeout(() => {
        setIsAddressLookupLoading(false);
        if (formData.address.trim().length >= 3) {
          setAddressLookupMessage('Address lookup timed out. Check your API key restrictions.');
        }
      }, 5000);

      addressAutocompleteServiceRef.current.getPlacePredictions(
        { input: query, types: ['geocode'] },
        (predictions: any[], status: any) => {
          window.clearTimeout(safetyTimeoutId);
          const placesStatus = (window as any).google?.maps?.places?.PlacesServiceStatus;
          if (status === placesStatus?.OK && Array.isArray(predictions)) {
            setAddressSuggestions(
              predictions.map((item) => ({
                place_id: item.place_id,
                description: item.description,
              }))
            );
            setAddressLookupMessage('');
          } else if (status === placesStatus?.ZERO_RESULTS) {
            setAddressSuggestions([]);
            setAddressLookupMessage('No matches found');
          } else {
            setAddressSuggestions([]);
            if (status === placesStatus?.REQUEST_DENIED) {
              setAddressLookupMessage('Request denied. Check key restrictions and enabled APIs.');
            } else if (status === placesStatus?.OVER_QUERY_LIMIT) {
              setAddressLookupMessage('Query limit reached for Google Places API key.');
            } else if (status === placesStatus?.INVALID_REQUEST) {
              setAddressLookupMessage('Invalid request sent to Google Places.');
            } else {
              setAddressLookupMessage('Address lookup unavailable right now.');
            }
          }
          setIsAddressLookupLoading(false);
        }
      );
    }, 250);

    return () => {
      if (addressInputDebounceRef.current) {
        window.clearTimeout(addressInputDebounceRef.current);
      }
    };
  }, [formData.address, isPlacesReady, hasPlacesApiKey]);

  // Fetch address suggestions for Wind Report / Weather Verification as user types (debounced).
  useEffect(() => {
    if (!isPlacesReady || !hasPlacesApiKey) {
      return;
    }
    const query = weatherGenForm.address.trim();
    if (query.length < 3) {
      setWvAddressSuggestions([]);
      setWvAddressLookupMessage('');
      setIsWvAddressLookupLoading(false);
      return;
    }
    if (!addressAutocompleteServiceRef.current) {
      return;
    }

    if (wvAddressInputDebounceRef.current) {
      window.clearTimeout(wvAddressInputDebounceRef.current);
    }

    setIsWvAddressLookupLoading(true);
    setWvAddressLookupMessage('');
    wvAddressInputDebounceRef.current = window.setTimeout(() => {
      const safetyTimeoutId = window.setTimeout(() => {
        setIsWvAddressLookupLoading(false);
        if (weatherGenForm.address.trim().length >= 3) {
          setWvAddressLookupMessage('Address lookup timed out. Check your API key restrictions.');
        }
      }, 5000);

      addressAutocompleteServiceRef.current.getPlacePredictions(
        { input: query, types: ['geocode'] },
        (predictions: any[], status: any) => {
          window.clearTimeout(safetyTimeoutId);
          const placesStatus = (window as any).google?.maps?.places?.PlacesServiceStatus;
          if (status === placesStatus?.OK && Array.isArray(predictions)) {
            setWvAddressSuggestions(
              predictions.map((item) => ({
                place_id: item.place_id,
                description: item.description,
              }))
            );
            setWvAddressLookupMessage('');
          } else if (status === placesStatus?.ZERO_RESULTS) {
            setWvAddressSuggestions([]);
            setWvAddressLookupMessage('No matches found');
          } else {
            setWvAddressSuggestions([]);
            if (status === placesStatus?.REQUEST_DENIED) {
              setWvAddressLookupMessage('Request denied. Check key restrictions and enabled APIs.');
            } else if (status === placesStatus?.OVER_QUERY_LIMIT) {
              setWvAddressLookupMessage('Query limit reached for Google Places API key.');
            } else if (status === placesStatus?.INVALID_REQUEST) {
              setWvAddressLookupMessage('Invalid request sent to Google Places.');
            } else {
              setWvAddressLookupMessage('Address lookup unavailable right now.');
            }
          }
          setIsWvAddressLookupLoading(false);
        }
      );
    }, 250);

    return () => {
      if (wvAddressInputDebounceRef.current) {
        window.clearTimeout(wvAddressInputDebounceRef.current);
      }
    };
  }, [weatherGenForm.address, isPlacesReady, hasPlacesApiKey]);

  // Hide suggestions when clicking outside.
  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!addressInputRef.current?.contains(target) && !addressSuggestionsRef.current?.contains(target)) {
        setShowAddressSuggestions(false);
      }
      if (!wvAddressInputRef.current?.contains(target) && !wvAddressSuggestionsRef.current?.contains(target)) {
        setShowWvAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, []);

  const handleAddressSuggestionSelect = (description: string) => {
    setFormData((prev) => ({
      ...prev,
      address: description,
    }));
    setAddressSuggestions([]);
    setAddressLookupMessage('');
    setShowAddressSuggestions(false);
  };

  const handleWvAddressSuggestionSelect = (description: string) => {
    setWeatherGenForm((prev) => ({
      ...prev,
      address: description,
    }));
    setWvAddressSuggestions([]);
    setWvAddressLookupMessage('');
    setShowWvAddressSuggestions(false);
  };

  const handleSaveSVG = async () => {
    let svgContent = svgData;
    
    // If no SVG data but we have an uploaded file, read the file content
    if (!svgContent && uploadedSvgFile) {
      try {
        svgContent = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            resolve(event.target?.result as string);
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsText(uploadedSvgFile);
        });
      } catch (error) {
        alert('Error reading uploaded SVG file. Please try again.');
        return;
      }
    }
    
    if (!svgContent) {
      alert('No SVG data to save. Please upload a file or create a drawing first.');
      return;
    }

    // Auto-crop and compute tight viewBox extents around drawing entities
    svgContent = computeTightSvgViewBox(svgContent);

    try {
      setLoading(true);
      
      const response = await axios.post(`${API_URL}/save_svg`, {
        svg: svgContent
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true
      });

      if (response.data.success) {
        const previewUrl = URL.createObjectURL(new Blob([svgContent], { type: 'image/svg+xml' }));
        
        const newSvg = {
          name: response.data.filename,
          data: svgContent,
          preview: previewUrl
        };
        setSavedSvgs(prev => [...prev, newSvg]);
        
        alert(`Drawing saved successfully as ${response.data.filename}`);
      } else {
        alert('Failed to save drawing');
      }
    } catch (error) {
      console.error('Error saving SVG:', error);
      alert('Error saving drawing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSVG = () => {
    setSvgData('');
    setUploadedSvgFile(null);
  };

  const handleDeleteSVG = async (index: number) => {
    const svgToDelete = savedSvgs[index];
    
    try {
      const response = await axios.delete(`${API_URL}/delete_svg/${svgToDelete.name}`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setSavedSvgs(prev => prev.filter((_, i) => i !== index));
      } else {
        alert('Failed to delete drawing');
      }
    } catch (error) {
      console.error('Error deleting SVG:', error);
      alert('Error deleting drawing. Please try again.');
    }
  };


  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'svg-save') {
        const svgContent = event.data.svg;
        console.log('Received SVG data from editor:', svgContent ? 'SVG data received' : 'No SVG data');
        
        if (svgContent && svgContent.trim()) {
          setSvgData(svgContent);
          setIsEditorOpen(false);
          
          // Save the SVG immediately with the received data
          setTimeout(async () => {
            try {
              setLoading(true);
              
              // Check if we're editing an existing SVG
              if (editingSvg) {
                // Update existing SVG file
                const response = await axios.put(`${API_URL}/update_svg/${editingSvg.name}`, {
                  svg: svgContent
                }, {
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  withCredentials: true
                });

                if (response.data.success) {
                  const previewUrl = `data:image/svg+xml;base64,${btoa(svgContent)}`;
                  
                  // Update the existing SVG in the list
                  setSavedSvgs(prev => prev.map(svg => 
                    svg.name === editingSvg.name 
                      ? { ...svg, data: svgContent, preview: previewUrl }
                      : svg
                  ));
                  
                  setEditingSvg(null);
                  alert(`Drawing updated successfully`);
                } else {
                  alert('Failed to update drawing');
                }
              } else {
                // Create new SVG file
                const response = await axios.post(`${API_URL}/save_svg`, {
                  svg: svgContent
                }, {
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  withCredentials: true
                });

                if (response.data.success) {
                  const previewUrl = `data:image/svg+xml;base64,${btoa(svgContent)}`;
                  
                  const newSvg = {
                    name: response.data.filename,
                    data: svgContent,
                    preview: previewUrl
                  };
                  setSavedSvgs(prev => [...prev, newSvg]);
                  
                  alert(`Drawing saved successfully as ${response.data.filename}`);
                } else {
                  alert('Failed to save drawing');
                }
              }
            } catch (error) {
              console.error('Error saving SVG:', error);
              alert('Error saving drawing. Please try again.');
            } finally {
              setLoading(false);
            }
          }, 100);
        } else {
          alert('No SVG data to save. Please create a drawing first.');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [editingSvg]);

  const getAvailableAreas = () => {
    const areas = ['exterior', 'basement'];
    
    if (formData.structure_type === 'multi') {
      for (let i = 1; i <= formData.total_floors; i++) {
        areas.push(`floor${i}`);
      }
    } else {
      areas.push('floor1');
    }
    
    areas.push('roof');
    return areas;
  };

  const getAreaDisplayName = (area: string) => {
    switch (area) {
      case 'exterior': return 'Exterior';
      case 'basement': return 'Basement';
      case 'roof': return 'Roof';
      default:
        if (area.startsWith('floor')) {
          const floorNum = area.replace('floor', '');
          return `Floor ${floorNum}`;
        }
        return area;
    }
  };

  const appendImagesByArea = useCallback((byArea: Record<string, File[]>) => {
    setImageCollections((prev) => {
      const next = { ...prev };
      for (const [area, flist] of Object.entries(byArea)) {
        if (flist.length === 0) continue;
        const newDescriptions = new Array(flist.length).fill('');
        next[area] = {
          files: [...(next[area]?.files || []), ...flist],
          descriptions: [...(next[area]?.descriptions || []), ...newDescriptions],
        };
      }
      return next;
    });
  }, []);

  const appendClassifiedImages = useCallback(
    (
      files: File[],
      rows: { filename: string; area: string; description: string }[],
    ) => {
      const areas: string[] = ['exterior', 'basement'];
      if (formData.structure_type === 'multi') {
        for (let i = 1; i <= formData.total_floors; i++) {
          areas.push(`floor${i}`);
        }
      } else {
        areas.push('floor1');
      }
      areas.push('roof');
      const validAreas = new Set(areas);
      const byArea: Record<string, { files: File[]; descriptions: string[] }> = {};
      files.forEach((file, i) => {
        const row = rows[i];
        if (!row) return;
        let area = row.area;
        if (!validAreas.has(area)) area = 'exterior';
        if (!byArea[area]) byArea[area] = { files: [], descriptions: [] };
        byArea[area].files.push(file);
        byArea[area].descriptions.push(row.description || '');
      });
      setImageCollections((prev) => {
        const next = { ...prev };
        for (const [area, bundle] of Object.entries(byArea)) {
          if (bundle.files.length === 0) continue;
          next[area] = {
            files: [...(next[area]?.files || []), ...bundle.files],
            descriptions: [...(next[area]?.descriptions || []), ...bundle.descriptions],
          };
        }
        return next;
      });
    },
    [formData.structure_type, formData.total_floors],
  );

  const removeFilesFromCollections = useCallback((filesToRemove: File[]) => {
    const removeSet = new Set(filesToRemove);
    setImageCollections((prev) => {
      const next = { ...prev };
      for (const [area, coll] of Object.entries(prev)) {
        if (!coll?.files?.length) continue;
        const keepIdx: number[] = [];
        coll.files.forEach((f, i) => {
          if (!removeSet.has(f)) keepIdx.push(i);
        });
        if (keepIdx.length === coll.files.length) continue;
        next[area] = {
          files: keepIdx.map((i) => coll.files[i]),
          descriptions: keepIdx.map((i) => coll.descriptions[i] ?? ''),
        };
      }
      return next;
    });
  }, []);

  const onManualLabelingClick = useCallback(
    (b: FolderLabelingBanner) => {
      if (b.kind === 'partial') {
        removeFilesFromCollections(b.failedFiles);
        setStagedImagesForLabeling(b.failedFiles);
      }
      setImageLabelChoiceOpen(false);
      setFolderImageLabelOpen(true);
      setLabelingBanner(null);
    },
    [removeFilesFromCollections],
  );

  const runAutomaticFolderLabeling = async () => {
    const labelable = stagedImagesForLabeling.filter((f) => /\.(jpe?g|png)$/i.test(f.name));
    const skippedFiles = stagedImagesForLabeling.filter((f) => !/\.(jpe?g|png)$/i.test(f.name));

    if (labelable.length === 0) {
      setFolderImportFeedback(
        'Automatic labeling needs JPG or PNG photos. Use manual labeling for other formats, or convert files.',
      );
      setImageLabelChoiceOpen(false);
      return;
    }

    setLabelingBanner(null);
    setFolderAutoLabelBusy(true);
    setFolderImportFeedback(`Labeling ${labelable.length} photo(s) with AI…`);

    type ClassifyRow = { filename: string; area: string; description: string; category?: string };

    const classifyBatchMaxImages = 20;
    const classifyBatchMaxBytes = 25 * 1024 * 1024;
    const classifyBatches: File[][] = [];
    let curBatch: File[] = [];
    let curBytes = 0;
    for (const f of labelable) {
      if (
        curBatch.length >= classifyBatchMaxImages ||
        (curBytes + f.size > classifyBatchMaxBytes && curBatch.length > 0)
      ) {
        classifyBatches.push(curBatch);
        curBatch = [];
        curBytes = 0;
      }
      curBatch.push(f);
      curBytes += f.size;
    }
    if (curBatch.length > 0) classifyBatches.push(curBatch);

    const pollClassifyJob = async (jobId: string): Promise<ClassifyRow[]> => {
      const deadline = Date.now() + 30 * 60 * 1000;
      while (Date.now() < deadline) {
        const statusRes = await axios.get(`${API_URL}/api/analysis/classify-folder-images/${jobId}`, {
          withCredentials: true,
          headers: { ...getAuthHeaders() },
        });
        const d = statusRes.data as {
          success?: boolean;
          status?: string;
          results?: ClassifyRow[];
          error?: string;
        };
        if (!d.success) {
          throw new Error(d.error || 'Could not check labeling status');
        }
        if (d.status === 'completed' && Array.isArray(d.results)) {
          return d.results;
        }
        if (d.status === 'failed') {
          throw new Error(d.error || 'Automatic labeling failed');
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      throw new Error('Automatic labeling timed out. Try again with fewer photos or later.');
    };

    let labelingT0 = 0;
    try {
      labelingT0 = performance.now();
      const allResults: ClassifyRow[] = [];
      for (let bi = 0; bi < classifyBatches.length; bi++) {
        const batch = classifyBatches[bi];
        if (classifyBatches.length > 1) {
          setFolderImportFeedback(
            `Labeling photos (batch ${bi + 1} of ${classifyBatches.length}, ${labelable.length} total)…`,
          );
        }
        const fd = new FormData();
        batch.forEach((f) => fd.append('images', f));
        fd.append('structure_type', formData.structure_type);
        fd.append('total_floors', String(formData.total_floors));

        const response = await axios.post(`${API_URL}/api/analysis/classify-folder-images`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() },
        });

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Classification failed');
        }

        let batchResults: ClassifyRow[];
        if (response.status === 202 && response.data.job_id) {
          batchResults = await pollClassifyJob(String(response.data.job_id));
        } else {
          batchResults = (response.data.results || []) as ClassifyRow[];
        }
        allResults.push(...batchResults);
      }

      const failedFiles = labelable.filter((f) =>
        allResults.some((r) => r.filename === f.name && (r.category || '') === 'Unclassified'),
      );

      appendClassifiedImages(labelable, allResults);

      if (skippedFiles.length > 0) {
        appendImagesByArea({ exterior: skippedFiles });
      }

      let msg = `Added ${labelable.length} photo(s) with AI-assigned areas and captions.`;
      const totalMs = performance.now() - labelingT0;
      msg += ` Total time: ${formatAutomaticLabelingDuration(totalMs)} (${classifyBatches.length} batch${classifyBatches.length === 1 ? '' : 'es'}).`;
      if (skippedFiles.length > 0) {
        msg += ` ${skippedFiles.length} other image file(s) were added to Exterior without AI (JPG/PNG only).`;
      }
      setImageLabelChoiceOpen(false);
      setFolderImportFeedback(msg);
      setStagedImagesForLabeling([]);
      if (failedFiles.length > 0) {
        setLabelingBanner({
          kind: 'partial',
          text: `${msg} ${failedFiles.length} photo(s) could not be auto-analyzed — use manual assignment to place them correctly.`,
          failedFiles,
        });
      } else {
        setLabelingBanner({ kind: 'success', text: msg });
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      const serverMsg = ax.response?.data?.error;
      const errText =
        typeof serverMsg === 'string' ? serverMsg : ax.message || 'Automatic labeling failed.';
      const elapsedMs = labelingT0 > 0 ? performance.now() - labelingT0 : 0;
      const elapsedHint =
        elapsedMs > 0 ? ` Stopped after ${formatAutomaticLabelingDuration(elapsedMs)}.` : '';
      const fullErr = `${errText}${elapsedHint}`.trim();
      setFolderImportFeedback(fullErr);
      setLabelingBanner({ kind: 'error', text: fullErr });
    } finally {
      setFolderAutoLabelBusy(false);
    }
  };

  /**
   * Shared import logic used by both folder drag-and-drop and the file picker.
   * Classifies files and routes them to the right section (PDF, audio, SVG, photos).
   */
  const processImportedFiles = async (raw: File[]) => {
    setFolderImportFeedback('');
    try {
      setFolderImportBusy(true);
      if (raw.length === 0) {
        setFolderImportFeedback('No files found in the drop.');
        return;
      }
      const classified = classifyFiles(raw);
      const parts: string[] = [];

      if (classified.pdfs.length > 0) {
        setShowWindSection(true);
        setSelectedWindReport(classified.pdfs[0]);
        parts.push(`Wind report: ${classified.pdfs[0].name}`);
        if (classified.pdfs.length > 1) {
          parts.push(`${classified.pdfs.length - 1} extra PDF(s) skipped (first wins)`);
        }
      }

      if (classified.audio.length > 0) {
        setShowAudioSection(true);
        setSelectedAudio(classified.audio[0]);
        parts.push(`Audio: ${classified.audio[0].name}`);
        if (classified.audio.length > 1) {
          parts.push(`${classified.audio.length - 1} extra audio file(s) skipped (first wins)`);
        }
      }

      if (classified.svgs.length > 0) {
        setShowSvgSection(true);
        let svgOk = 0;
        let svgFail = 0;
        for (const sf of classified.svgs) {
          const r = await ingestSvgFromFile(sf, { showAlerts: false, useGlobalLoading: false });
          if (r === 'ok') svgOk += 1;
          else svgFail += 1;
        }
        if (svgOk > 0) parts.push(`${svgOk} SVG(s) saved`);
        if (svgFail > 0) parts.push(`${svgFail} SVG(s) failed or invalid`);
      }

      if (classified.other.length > 0) {
        parts.push(`${classified.other.length} unsupported file(s) skipped`);
      }

      if (classified.cad.length > 0) {
        setShowSvgSection(true);
        setPendingCadFile(classified.cad[0]);
        setIsCadViewerOpen(true);
        parts.push(`${classified.cad.length} CAD file(s) — opening in CAD Viewer`);
      }

      if (classified.images.length > 0) {
        setStagedImagesForLabeling(classified.images);
        setImageLabelChoiceOpen(true);
        parts.push(
          `${classified.images.length} photo(s) — choose manual or automatic labeling`
        );
      }

      if (parts.length > 0) {
        setFolderImportFeedback(parts.join(' · '));
      }
    } catch (err) {
      console.error('Folder import failed', err);
      setFolderImportFeedback('Could not read dropped folder. Try again or use file picker.');
    } finally {
      setFolderImportBusy(false);
    }
  };

  const handleFolderDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFolderDropActive(false);
    const raw = await collectFilesFromDataTransfer(e.dataTransfer);
    await processImportedFiles(raw);
  };

  const handleFolderPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    void processImportedFiles(files);
    // Reset value so the same file/folder can be selected again later.
    e.target.value = '';
  };

  const ANALYSIS_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  const CHUNKED_FLOW_THRESHOLD_BYTES = 28 * 1024 * 1024; // 28 MB
  const CHUNKED_FLOW_THRESHOLD_IMAGES = 20;
  const UPLOAD_BATCH_MAX_BYTES = 25 * 1024 * 1024; // 25 MB per batch
  const UPLOAD_BATCH_MAX_IMAGES = 20;
  const UPLOAD_BATCH_TIMEOUT_MS = 2 * 60 * 1000; // 2 min per batch

  const processAnalysisResponse = (response: any) => {
    if (!response.data.success) {
      setError('Analysis failed');
      return;
    }
    if (response.data.step === 'draft' && response.data.draft_report) {
      skipPersistDraftRef.current = true;
      if (user?.id) clearAnalysisDraftForUser(user.id);
      const draft = response.data.draft_report as Record<string, unknown>;
      const types: Record<string, 'string' | 'array' | 'object'> = {};
      Object.entries(draft).forEach(([k, v]) => {
        if (typeof v === 'string') types[k] = 'string';
        else if (Array.isArray(v)) types[k] = 'array';
        else if (v !== null && typeof v === 'object') types[k] = 'object';
        else types[k] = 'string';
      });
      navigate('/analysis/review', {
        state: {
          draftReport: draft,
          draftCaseNumber: response.data.case_number,
          draftImageCaptions: response.data.image_captions ?? {},
          draftImageFilenames: response.data.image_filenames ?? [],
          draftAerialFilenames: response.data.aerial_filenames ?? [],
          draftValueTypes: types,
          researchContext: response.data.research_context ?? '',
          transcript: response.data.transcript ?? '',
          engineerNotes: response.data.engineer_notes ?? '',
          windReport: response.data.wind_report ?? null,
        },
      });
      return;
    }
    skipPersistDraftRef.current = true;
    if (user?.id) clearAnalysisDraftForUser(user.id);
    setResults({
      case_number: response.data.case_number,
      report_download_url: `${API_URL}/api/reports/${response.data.case_number}/download`,
      success: true
    });
    alert(`Analysis completed successfully! Claim number: ${response.data.case_number}`);
  };

  const totalImages = Object.values(imageCollections).reduce((total, collection) => total + collection.files.length, 0);
  const canProceedFromStep1 = Boolean(formData.case_number.trim() && formData.address.trim());
  const canProceedFromStep2 = totalImages > 0;

  const runAnalysis = async () => {
    if (!formData.case_number.trim() || !formData.address.trim()) {
      setError('Please add a claim number and address before running analysis.');
      setCurrentStep(1);
      return;
    }
    if (totalImages === 0) {
      setError('Please upload at least one image before running analysis.');
      setCurrentStep(2);
      return;
    }
    setLoading(true);
    setError('');
    setAnalysisStartedCaseNumber(null);
    setStatus('Starting analysis...');

    const imageList: { file: File; description: string; area: string }[] = [];
    Object.entries(imageCollections).forEach(([area, collection]) => {
      collection.files.forEach((file, fileIndex) => {
        imageList.push({
          file,
          description: collection.descriptions[fileIndex] || '',
          area,
        });
      });
    });
    const totalImageCount = imageList.length;
    let estimatedBytes = 50 * 1024;
    imageList.forEach(({ file }) => { estimatedBytes += file.size; });
    if (selectedAudio) estimatedBytes += selectedAudio.size;
    if (selectedWindReport) estimatedBytes += selectedWindReport.size;
    savedSvgs.forEach((svg) => { estimatedBytes += (svg.data?.length || 0) * 2; });

    const useChunkedFlow = estimatedBytes > CHUNKED_FLOW_THRESHOLD_BYTES || totalImageCount > CHUNKED_FLOW_THRESHOLD_IMAGES;

    try {
      if (useChunkedFlow) {
        setStatus('Creating case...');
        await axios.post(`${API_URL}/api/analysis/prepare`, {
          case_number: formData.case_number,
          address: formData.address,
          date_start: formData.date_start || null,
          date_end: formData.date_end || null,
          policy_number: formData.policy_number || '',
          project_number: formData.project_number || '',
          engineer_notes: formData.engineer_notes || '',
          structure_type: formData.structure_type,
          total_floors: formData.total_floors,
        }, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          withCredentials: true,
        });

        const batches: { file: File; description: string; area: string }[][] = [];
        let currentBatch: { file: File; description: string; area: string }[] = [];
        let currentSize = 0;
        for (const item of imageList) {
          const exceedsBatchSize = currentSize + item.file.size > UPLOAD_BATCH_MAX_BYTES;
          const exceedsBatchCount = currentBatch.length >= UPLOAD_BATCH_MAX_IMAGES;
          if ((exceedsBatchSize || exceedsBatchCount) && currentBatch.length > 0) {
            batches.push(currentBatch);
            currentBatch = [];
            currentSize = 0;
          }
          currentBatch.push(item);
          currentSize += item.file.size;
        }
        if (currentBatch.length > 0) batches.push(currentBatch);

        for (let i = 0; i < batches.length; i++) {
          setStatus(`Uploading images (batch ${i + 1} of ${batches.length})...`);
          const batchForm = new FormData();
          batchForm.append('case_number', formData.case_number);
          batches[i].forEach(({ file, description, area }) => {
            batchForm.append('images', file);
            batchForm.append('image_descriptions', description);
            batchForm.append('image_areas', area);
          });
          await axios.post(`${API_URL}/api/analysis/upload-images`, batchForm, {
            headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() },
            withCredentials: true,
            timeout: UPLOAD_BATCH_TIMEOUT_MS,
          });
        }

        setStatus('Starting analysis...');
        const runForm = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          runForm.append(key, String(value));
        });
        runForm.append('use_uploaded_images', '1');
        if (selectedAudio) runForm.append('audio', selectedAudio);
        if (selectedWindReport) runForm.append('wind_report', selectedWindReport);
        savedSvgs.forEach((svg, index) => {
          runForm.append(`saved_svg_${index}`, svg.data);
          runForm.append(`saved_svg_name_${index}`, svg.name);
        });
        const response = await axios.post(`${API_URL}/api/analysis/run`, runForm, {
          headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() },
          withCredentials: true,
          timeout: ANALYSIS_TIMEOUT_MS,
        });
        if (response.status === 202 && response.data?.started === true && response.data?.case_number) {
          setStatus('Analysis started. You can close this page and check Cases for when it\'s ready to review.');
          setAnalysisStartedCaseNumber(response.data.case_number);
        } else if (response.status === 200 && response.data?.step === 'draft') {
          setStatus('Analysis completed successfully!');
          processAnalysisResponse(response);
        } else {
          setStatus('Analysis completed successfully!');
          processAnalysisResponse(response);
        }
      } else {
        const formDataToSend = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          formDataToSend.append(key, String(value));
        });
        Object.entries(imageCollections).forEach(([area, collection]) => {
          collection.files.forEach((file, fileIndex) => {
            formDataToSend.append('images', file);
            formDataToSend.append('image_descriptions', collection.descriptions[fileIndex] || '');
            formDataToSend.append('image_areas', area);
          });
        });
        if (selectedAudio) formDataToSend.append('audio', selectedAudio);
        if (selectedWindReport) formDataToSend.append('wind_report', selectedWindReport);
        savedSvgs.forEach((svg, index) => {
          formDataToSend.append(`saved_svg_${index}`, svg.data);
          formDataToSend.append(`saved_svg_name_${index}`, svg.name);
        });
        setStatus('Processing images and data...');
        const response = await axios.post(`${API_URL}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() },
          withCredentials: true,
          timeout: ANALYSIS_TIMEOUT_MS,
        });
        if (response.status === 202 && response.data?.started === true && response.data?.case_number) {
          setStatus('Analysis started. You can close this page and check Cases for when it\'s ready to review.');
          setAnalysisStartedCaseNumber(response.data.case_number);
        } else if (response.status === 200 && response.data?.step === 'draft') {
          setStatus('Analysis completed successfully!');
          processAnalysisResponse(response);
        } else {
          setStatus('Analysis completed successfully!');
          processAnalysisResponse(response);
        }
      }
    } catch (error: any) {
      console.error('Analysis error:', error);
      const statusCode = error.response?.status;
      const isTimeout = error.code === 'ECONNABORTED' || statusCode === 504;
      const msg = isTimeout
        ? 'Analysis timed out. The server may still be processing — check Cases in a moment.'
        : (error.response?.data?.error || 'Analysis failed');
      setError(msg);
      setAnalysisStartedCaseNumber(null);
    } finally {
      setLoading(false);
      setStatus('');
    }
  };

  return (
    <div className="analysis-form-page">
      <Navigation />
      <div className="analysis-container">
        {/* Hero/Intro Section */}
        <div className="hero-section">
          <div className="hero-content">
            <div className="hero-left">
              <h1 className="hero-title">
                AI Forensic Analysis
              </h1>
              <div
                className={`file-upload-area analysis-folder-drop analysis-hero-folder-drop${folderDropActive ? ' file-upload-area--drag-active' : ''}${folderImportBusy || folderAutoLabelBusy ? ' analysis-folder-drop--busy' : ''}`}
                onDragEnter={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  setFolderDropActive(true);
                }}
                onDragOver={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                }}
                onDragLeave={(ev) => {
                  ev.preventDefault();
                  if (!ev.currentTarget.contains(ev.relatedTarget as Node)) {
                    setFolderDropActive(false);
                  }
                }}
                onDrop={handleFolderDrop}
              >
                <input
                  ref={folderPickerRef}
                  type="file"
                  multiple
                  onChange={handleFolderPickerChange}
                  disabled={folderImportBusy || folderAutoLabelBusy}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    padding: 0,
                    margin: 0,
                    opacity: 0,
                    cursor: 'pointer',
                    border: 0,
                  }}
                  aria-label="Import folder"
                  tabIndex={0}
                />
                <div className="file-upload-content analysis-hero-folder-drop-content">
                  <i className="fas fa-folder-open" aria-hidden />
                  <div className="analysis-hero-folder-drop-text">
                    <p>Drop folder to import</p>
                    <span>PDF, audio, SVG, photos</span>
                  </div>
                </div>
                {folderImportBusy && (
                  <div className="analysis-folder-drop-busy" aria-busy="true">
                    <i className="fas fa-spinner fa-spin" aria-hidden />
                    <span>Importing…</span>
                  </div>
                )}
              </div>
              {folderImportFeedback && (
                <div
                  className="status-message folder-import-feedback analysis-hero-folder-feedback"
                  role="status"
                >
                  <p>{folderImportFeedback}</p>
                </div>
              )}
            </div>
            <div className="hero-right">
              <p className="hero-description">
                Create comprehensive forensic analysis reports using advanced AI models. Upload images, audio files, 
                and structural drawings to generate detailed case documentation with intelligent pattern recognition 
                and evidence analysis.
              </p>
              <div className="hero-actions">
                {/* <button 
                  onClick={() => navigate('/cases')}
                  className="hero-cta-btn secondary"
                >
                  <i className="fas fa-folder-open"></i>
                  View Cases
                </button> */}
              </div>
            </div>
          </div>
          <div className="hero-pattern"></div>
        </div>

        {/* Form Content Card */}
        <div className="form-content-card">
          <div className="form-container">
            <form className="analysis-form">
          <div className="analysis-stepper">
            <div className="analysis-stepper-header">
              {[1, 2, 3].map((stepNumber) => {
                const stepTitle =
                  stepNumber === 1 ? 'Case info' : stepNumber === 2 ? 'Photos' : 'Review & run';
                const isCurrent = currentStep === stepNumber;
                const isComplete = currentStep > stepNumber;
                return (
                  <button
                    key={stepNumber}
                    type="button"
                    className={`stepper-step ${isCurrent ? 'current' : ''} ${isComplete ? 'complete' : ''}`}
                    onClick={() => setCurrentStep(stepNumber as 1 | 2 | 3)}
                  >
                    <span className="stepper-index">{stepNumber}</span>
                    <span className="stepper-label">{stepTitle}</span>
                  </button>
                );
              })}
            </div>

            {currentStep === 1 && (
              <>
          <div className="form-section required-fields-card">
            <h2>Required Information</h2>

            <div className="required-block">
              <h3 className="required-block-title">Case Details</h3>

              <div className="form-row case-details-ids-row">
                <div className="form-group">
                  <label htmlFor="case_number">Claim number *</label>
                  <input
                    type="text"
                    id="case_number"
                    name="case_number"
                    value={formData.case_number}
                    onChange={handleInputChange}
                    placeholder="e.g. CLAIM-2024-001"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="policy_number">Policy number</label>
                  <input
                    type="text"
                    id="policy_number"
                    name="policy_number"
                    value={formData.policy_number}
                    onChange={handleInputChange}
                    placeholder="Optional"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="project_number">Project number</label>
                  <input
                    type="text"
                    id="project_number"
                    name="project_number"
                    value={formData.project_number}
                    onChange={handleInputChange}
                    placeholder="Optional"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="case-details-dates-wrap">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="date_start">Start date *</label>
                    <input
                      type="date"
                      id="date_start"
                      name="date_start"
                      value={formData.date_start}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="date_end">End date</label>
                    <input
                      type="date"
                      id="date_end"
                      name="date_end"
                      value={formData.date_end}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address *</label>
                <div className="address-autocomplete-wrapper" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    onFocus={() => setShowAddressSuggestions(true)}
                    ref={addressInputRef}
                    placeholder="Enter full address"
                    className="form-input"
                    autoComplete="street-address"
                    style={{ paddingRight: '4.6rem' }}
                  />
                  <AddressPicker
                    initialAddress={formData.address}
                    onSelect={(address) => {
                      setFormData((prev: any) => ({ ...prev, address }));
                      setShowAddressSuggestions(false);
                    }}
                  />
                  {showAddressSuggestions && hasPlacesApiKey && (
                    <div className="address-suggestions-dropdown" ref={addressSuggestionsRef}>
                      {isAddressLookupLoading && (
                        <div className="address-suggestion-item address-suggestion-state">Searching addresses...</div>
                      )}
                      {!isAddressLookupLoading && addressLookupMessage && formData.address.trim().length >= 3 && (
                        <div className="address-suggestion-item address-suggestion-state">{addressLookupMessage}</div>
                      )}
                      {!isAddressLookupLoading && addressSuggestions.map((suggestion) => (
                        <button
                          type="button"
                          key={suggestion.place_id}
                          className="address-suggestion-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleAddressSuggestionSelect(suggestion.description);
                          }}
                        >
                          {suggestion.description}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <small className="input-help-text">
                  {hasPlacesApiKey
                    ? ''
                    : 'Google Places suggestions are disabled (missing REACT_APP_GOOGLE_PLACES_API_KEY).'}
                </small>
              </div>
            </div>
          </div>

          <div className="advanced-chip-row">
            <span className="advanced-chip-label">Add optional info:</span>
            <button
              type="button"
              className={`advanced-chip ${showNotes ? 'active' : ''}`}
              onClick={() => setShowNotes((prev) => !prev)}
            >
              <i className="fas fa-sticky-note"></i>
              Engineer notes
            </button>
            <button
              type="button"
              className={`advanced-chip ${showAudioSection ? 'active' : ''}`}
              onClick={() => setShowAudioSection((prev) => !prev)}
            >
              <i className="fas fa-microphone"></i>
              Site visit
            </button>
            <button
              type="button"
              className={`advanced-chip ${showWindSection ? 'active' : ''}`}
              onClick={() => setShowWindSection((prev) => !prev)}
            >
              <i className="fas fa-wind"></i>
              Wind report
            </button>
            <button
              type="button"
              className={`advanced-chip ${showSvgSection ? 'active' : ''}`}
              onClick={() => setShowSvgSection((prev) => !prev)}
            >
              <i className="fas fa-drafting-compass"></i>
              Diagrams
            </button>
          </div>

          {showNotes && (
          <div className="form-section optional-section">
            <h2>Engineer Notes</h2>
            <div className="form-group">
              <label htmlFor="engineer_notes">Engineer Notes</label>
              <textarea
                id="engineer_notes"
                name="engineer_notes"
                value={formData.engineer_notes}
                onChange={handleInputChange}
                rows={4}
                placeholder="Enter detailed notes about the case..."
                className="form-textarea"
              />
            </div>
          </div>
          )}

          {showAudioSection && (
          <div className="form-section optional-section">
            <h2>Site Visit Audio</h2>
            
            <div className="form-group">
              <label htmlFor="audio">Site Visit Recording</label>
              <div className="file-upload-area">
                <input
                  ref={audioInputRef}
                  type="file"
                  id="audio"
                  onChange={handleAudioChange}
                  accept="audio/*"
                  className="file-input"
                />
                <div className="file-upload-content">
                  <i className="fas fa-microphone"></i>
                  <p>Click to upload audio file</p>
                  <span>Supports: MP3, WAV, M4A</span>
                </div>
              </div>
              {selectedAudio && (
                <div className="selected-files">
                  <div className="selected-file-row">
                    <div>
                      <p>Selected audio: {selectedAudio.name}</p>
                      <p className="file-size">Size: {(selectedAudio.size / (1024 * 1024)).toFixed(2)} MB</p>
                    </div>
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={handleRemoveAudio}
                      title="Remove audio"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {showWindSection && (
          <div className="form-section optional-section">
            <h2>Weather & Wind Report</h2>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={openWeatherGenModal}
                disabled={loading || weatherGenLoading}
              >
                <i className="fas fa-cloud-sun" /> Generate weather verification report
              </button>
              <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                Build a NOAA-backed verification report, download as PDF, or attach it here for analysis (same as uploading a wind report PDF).
              </p>
            </div>
            
            <div className="form-group">
              <label htmlFor="wind_report">Upload Wind Report Document (PDF)</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  id="wind_report"
                  name="wind_report"
                  onChange={handleWindReportChange}
                  accept=".pdf,application/pdf"
                  className="file-input"
                />
                <div className="file-upload-content">
                  <i className="fas fa-file-pdf"></i>
                  <p>Click to upload wind report PDF</p>
                  <span>Supports: PDF files (max 10MB)</span>
                </div>
              </div>
              {selectedWindReport && (
                <div className="selected-files">
                  <p>Selected wind report: {selectedWindReport.name}</p>
                  <p className="file-size">Size: {(selectedWindReport.size / (1024 * 1024)).toFixed(2)} MB</p>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleExtractWindReport}
                    disabled={isExtractingWindReport}
                    style={{ marginTop: '0.75rem' }}
                  >
                    {isExtractingWindReport ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Extracting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-search"></i>
                        Extract Wind Data
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
          )}

          {showSvgSection && (
          <div className="form-section optional-section">
            <h2>SVG Drawing</h2>
            
            {/* SVG File Upload */}
            <div className="form-group">
              <label htmlFor="svg_file">Upload SVG File</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  id="svg_file"
                  onChange={handleSvgFileUpload}
                  accept=".svg,image/svg+xml"
                  className="file-input"
                />
                <div className="file-upload-content">
                  <i className="fas fa-file-upload"></i>
                  <p>Click to upload SVG file</p>
                  <span>Supports: .svg files (max 5MB)</span>
                </div>
              </div>
              {uploadedSvgFile && (
                <div className="selected-files">
                  <p>Selected SVG: {uploadedSvgFile.name}</p>
                </div>
              )}
            </div>

            {/* CAD File Upload (DWG/DXF) */}
            <div className="form-group">
              <label htmlFor="cad_file">Upload CAD File (DWG/DXF)</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  id="cad_file"
                  onChange={handleCadFileUpload}
                  accept=".dwg,.dxf"
                  className="file-input"
                />
                <div className="file-upload-content">
                  <i className="fas fa-drafting-compass"></i>
                  <p>Click to upload DWG or DXF file</p>
                  <span>Opens in built-in CAD Viewer — export SVG when ready</span>
                </div>
              </div>
            </div>
            
            
            <div className="svg-controls">
              {/* Only show Save SVG button if there's SVG data from editor (not from upload) */}
              {svgData && !uploadedSvgFile && (
                <button type="button" className="btn-secondary" onClick={handleSaveSVG} disabled={loading}>
                  <i className="fas fa-save"></i>
                  {loading ? 'Saving...' : 'Save SVG'}
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={handleOpenEditor}>
                <i className="fas fa-edit"></i>
                Open Editor
              </button>
              <button type="button" className="btn-secondary" onClick={handleOpenCadViewer}>
                <i className="fas fa-drafting-compass"></i>
                Open CAD Viewer
              </button>
              <button type="button" className="btn-secondary" onClick={handleClearSVG}>
                <i className="fas fa-trash"></i>
                Clear {uploadedSvgFile ? 'All' : ''}
              </button>
            </div>
            
            {/* Show current SVG source */}
            {(svgData || uploadedSvgFile) && (
              <div className="svg-source-info">
                <p>
                  <i className="fas fa-info-circle"></i>
                  SVG Source: {uploadedSvgFile ? `Uploaded file: ${uploadedSvgFile.name}` : 'Drawing editor'}
                </p>
              </div>
            )}

            {/* Saved SVG List with Previews */}
            {savedSvgs.length > 0 && (
              <div className="svg-preview-section">
                <h4>Saved SVG Drawings ({savedSvgs.length})</h4>
                <div className="svg-preview-grid">
                  {savedSvgs.map((svg, index) => (
                    <div key={index} className="svg-preview-item">
                      <div className="svg-preview-container" onClick={() => handleSvgPreview(svg)}>
                        <img 
                          src={svg.preview} 
                          alt={svg.name}
                          className="svg-preview-image"
                        />
                        <div className="svg-preview-overlay">
                          <i className="fas fa-search-plus"></i>
                          <span>Click to view</span>
                        </div>
                      </div>
                      <div className="svg-preview-info">
                        <span className="svg-preview-name">{svg.name}</span>
                        <div className="svg-preview-actions">
                          <button 
                            className="svg-download-link"
                            onClick={() => {
                              // Create a blob from the SVG data
                              const svgBlob = new Blob([svg.data], { type: 'image/svg+xml' });
                              const url = URL.createObjectURL(svgBlob);
                              
                              // Create download link
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = svg.name;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              
                              // Clean up the blob URL
                              URL.revokeObjectURL(url);
                            }}
                            title="Download SVG"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                          <button 
                            type="button"
                            className="svg-edit-btn"
                            onClick={() => handleEditSvg(svg)}
                            title="Edit SVG"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            type="button"
                            className="svg-delete-btn"
                            onClick={() => handleDeleteSVG(index)}
                            title="Delete drawing"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}
          </>
        )}

            {currentStep === 2 && (
              <div className="form-section">
                <h2>Photos by Area</h2>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="structure_type">Structure Type</label>
                    <select
                      id="structure_type"
                      name="structure_type"
                      value={formData.structure_type}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value="single">Single Story</option>
                      <option value="multi">Multi-Story</option>
                    </select>
                  </div>
                  {formData.structure_type === 'multi' && (
                    <div className="form-group">
                      <label htmlFor="total_floors">Number of Floors</label>
                      <select
                        id="total_floors"
                        name="total_floors"
                        value={formData.total_floors}
                        onChange={handleInputChange}
                        className="form-input"
                      >
                        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(floor => (
                          <option key={floor} value={floor}>{floor}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="area-selection">
                  <label>Select Area to Upload Images:</label>
                  <div className="area-buttons">
                    {getAvailableAreas().map(area => (
                      <button
                        key={area}
                        type="button"
                        className={`area-button ${currentArea === area ? 'active' : ''}`}
                        onClick={() => handleAreaChange(area)}
                      >
                        {getAreaDisplayName(area)}
                        {imageCollections[area]?.files.length > 0 && (
                          <span className="image-count">({imageCollections[area].files.length})</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="current-area-upload">
                  <h3>Upload Images for {getAreaDisplayName(currentArea)}</h3>

                  <div className="form-group">
                    <div className="file-upload-area">
                      <input
                        type="file"
                        onChange={handleImageChange}
                        multiple
                        accept="image/*"
                        className="file-input"
                      />
                      <div className="file-upload-content">
                        <i className="fas fa-cloud-upload-alt"></i>
                        <p>Click to upload images for {getAreaDisplayName(currentArea)}</p>
                        <span>Supports: JPG, PNG, GIF</span>
                      </div>
                    </div>
                  </div>

                  {imageCollections[currentArea]?.files.length > 0 && (
                    <div className="area-images">
                      <h4>Images for {getAreaDisplayName(currentArea)} ({imageCollections[currentArea].files.length})</h4>
                      <div className="image-grid">
                        {imageCollections[currentArea].files.map((file, index) => (
                          <div key={index} className="image-item">
                            <div className="image-preview-container" onClick={() => handleImageClick(file, currentArea, index)}>
                              <img src={URL.createObjectURL(file)} alt={file.name} className="image-preview" />
                              <div className="image-preview-overlay">
                                <i className="fas fa-search-plus"></i>
                                <span>Click to view</span>
                              </div>
                            </div>
                            <div className="image-info">
                              <span className="image-name">{file.name}</span>
                              <button
                                type="button"
                                className="remove-image-btn"
                                onClick={() => handleRemoveImage(currentArea, index)}
                                title="Remove image"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                            <div className="image-description">
                              <textarea
                                value={imageCollections[currentArea].descriptions[index] || ''}
                                onChange={(e) => handleDescriptionChange(currentArea, index, e.target.value)}
                                placeholder="Add description for this image..."
                                rows={2}
                                className="description-textarea"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="areas-summary">
                  <h3>Summary ({totalImages} total images)</h3>
                  <div className="areas-list">
                    {getAvailableAreas().map(area => {
                      const collection = imageCollections[area];
                      const imageCount = collection?.files.length || 0;
                      return (
                        <div key={area} className={`area-summary ${imageCount > 0 ? 'has-images' : 'no-images'}`}>
                          <span className="area-name">{getAreaDisplayName(area)}</span>
                          <span className="area-count">{imageCount} images</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="form-section review-section">
                <h2>Review & Run</h2>
                <div className="review-grid">
                  <div className="review-card">
                    <h3>Case Information</h3>
                    <p><strong>Claim number:</strong> {formData.case_number || 'Missing'}</p>
                    <p><strong>Policy number:</strong> {formData.policy_number || '—'}</p>
                    <p><strong>Project number:</strong> {formData.project_number || '—'}</p>
                    <p><strong>Date Range:</strong> {formData.date_start || formData.date_end ? `${formData.date_start || 'N/A'} to ${formData.date_end || 'N/A'}` : 'Not provided'}</p>
                    <p><strong>Address:</strong> {formData.address || 'Missing'}</p>
                    <p><strong>Total Photos:</strong> {totalImages}</p>
                  </div>
                  <div className="review-card">
                    <h3>Advanced</h3>
                    <p><strong>Engineer Notes:</strong> {formData.engineer_notes ? 'Provided' : 'Not provided'}</p>
                    <p><strong>Audio:</strong> {selectedAudio ? selectedAudio.name : 'Not provided'}</p>
                    <p><strong>Wind Report:</strong> {selectedWindReport ? selectedWindReport.name : 'Not provided'}</p>
                    <p><strong>SVG Drawings:</strong> {savedSvgs.length}</p>
                  </div>
                </div>
                <p className="review-note">
                  Analysis can take several minutes. After iniital processing, you can track progress in Cases. <b>You will be prompted when it is available.</b>
                </p>
              </div>
            )}

          {/* Action Buttons */}
          <div className="form-actions step-actions">
            {currentStep > 1 && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setCurrentStep((prev) => (prev - 1) as 1 | 2 | 3)}
              >
                <i className="fas fa-arrow-left"></i>
                Back
              </button>
            )}
            {currentStep < 3 ? (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setCurrentStep((prev) => (Math.min(3, (prev + 1)) as 1 | 2 | 3))}
              >
                Next
                <i className="fas fa-arrow-right"></i>
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary"
                disabled={loading || !canProceedFromStep1 || !canProceedFromStep2}
                onClick={runAnalysis}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-play"></i>
                    Start Analysis ({totalImages} images)
                  </>
                )}
              </button>
            )}
          </div>
          </div>
            </form>

            {/* Analysis started (202) - go to Cases to review when ready */}
            {analysisStartedCaseNumber && (
              <div className="status-message" style={{ marginTop: '1rem' }}>
                <p>Analysis started. You can close this page and check Cases for when it&apos;s ready to review.</p>
                <button
                  type="button"
                  onClick={() => navigate('/cases')}
                  className="hero-cta-btn secondary"
                  style={{ marginTop: '0.5rem' }}
                >
                  Go to Cases
                </button>
              </div>
            )}

            {/* Status Display */}
            {status && (
              <div className="status-message">
                <h3>Status</h3>
                <p>{status}</p>
              </div>
            )}
          </div>
        </div>

        <FolderLabelingStatusDock
          visible={folderAutoLabelBusy || labelingBanner !== null}
          running={folderAutoLabelBusy}
          runningMessage={folderImportFeedback}
          banner={folderAutoLabelBusy ? null : labelingBanner}
          onDismissBanner={() => setLabelingBanner(null)}
          onManualProcess={
            labelingBanner && (labelingBanner.kind === 'error' || labelingBanner.kind === 'partial')
              ? () => onManualLabelingClick(labelingBanner)
              : null
          }
        />

        {imageLabelChoiceOpen && (
          <div
            className="modal-overlay folder-import-choice-overlay"
            onClick={() => setImageLabelChoiceOpen(false)}
          >
            <div
              className="modal-content folder-import-choice-modal"
              onClick={(ev) => ev.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="folder-import-choice-title"
            >
              <div className="modal-header">
                <h3 id="folder-import-choice-title">Imported photos</h3>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setImageLabelChoiceOpen(false)}
                  aria-label="Close"
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>
              <div className="modal-body">
                <p className="folder-import-choice-copy">
                  {stagedImagesForLabeling.length} image
                  {stagedImagesForLabeling.length === 1 ? '' : 's'} found. How should we assign areas?
                </p>
                {folderAutoLabelBusy && (
                  <>
                    <p className="folder-import-choice-hint" role="status" aria-live="polite">
                      {folderImportFeedback}
                    </p>
                    <p className="folder-import-choice-hint folder-import-choice-hint--dismiss">
                      You can close this dialog — automatic labeling continues in the background. Check the message
                      under the folder drop zone or the status card at the bottom-right of the screen.
                    </p>
                  </>
                )}
                <div className="folder-import-choice-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={folderAutoLabelBusy}
                    onClick={() => {
                      void runAutomaticFolderLabeling();
                    }}
                  >
                    {folderAutoLabelBusy ? (
                      <>
                        <i className="fas fa-spinner fa-spin" aria-hidden />
                        Labeling…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-magic" aria-hidden />
                        Automatic labeling
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={folderAutoLabelBusy}
                    onClick={() => {
                      setImageLabelChoiceOpen(false);
                      setFolderImageLabelOpen(true);
                    }}
                  >
                    <i className="fas fa-hand-pointer" aria-hidden />
                    Manual labeling
                  </button>
                </div>
                <p className="folder-import-choice-hint">
                  Automatic uses Zhipu GLM vision (same as the forensic automation tool) to assign each JPG/PNG
                  to an area and caption. Manual lets you pick the area for each photo (10 per page).
                </p>
              </div>
            </div>
          </div>
        )}

        <FolderImageLabelModal
          open={folderImageLabelOpen}
          files={stagedImagesForLabeling}
          areaKeys={getAvailableAreas()}
          getAreaDisplayName={getAreaDisplayName}
          onClose={() => setFolderImageLabelOpen(false)}
          onApply={(byArea) => {
            appendImagesByArea(byArea);
            setFolderImageLabelOpen(false);
          }}
        />

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        )}

        {/* Results Display */}
        {results && (
          <div className="results-section">
            <h2>Analysis Complete</h2>
            <div className="case-info">
              <h3>Claim number: {results.case_number}</h3>
              <p>Your forensic analysis report has been generated successfully!</p>
            </div>
            
            <div className="download-section">
              <h3>Download Report</h3>
              <div className="download-buttons">
                <button
                  type="button"
                  onClick={() => handleDownloadReport(results.report_download_url, 'download')}
                  disabled={!!downloadReportLoading}
                  className="download-button primary"
                >
                  <i className="fas fa-download"></i>
                  {downloadReportLoading ? 'Downloading...' : 'Download Report'}
                </button>
              </div>
              <p className="download-note">
                Downloads use your current session so the report opens in the app.
              </p>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {isImageModalOpen && selectedImageForPreview && (
          <div className="modal-overlay" onClick={handleCloseImageModal}>
            <div className="modal-content image-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Image Preview: {selectedImageForPreview.file.name}</h3>
                <button className="modal-close" onClick={handleCloseImageModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body image-modal-body">
                <div className="image-preview-full">
                  <img 
                    src={URL.createObjectURL(selectedImageForPreview.file)} 
                    alt={selectedImageForPreview.file.name}
                    className="full-size-image"
                  />
                </div>
                <div className="image-preview-info">
                  <div className="info-item">
                    <strong>File Name:</strong> {selectedImageForPreview.file.name}
                  </div>
                  <div className="info-item">
                    <strong>File Size:</strong> {(selectedImageForPreview.file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                  <div className="info-item">
                    <strong>File Type:</strong> {selectedImageForPreview.file.type}
                  </div>
                  <div className="info-item">
                    <strong>Area:</strong> {getAreaDisplayName(selectedImageForPreview.area)}
                  </div>
                  <div className="info-item">
                    <strong>Description:</strong>
                    <textarea
                      value={imageCollections[selectedImageForPreview.area]?.descriptions[selectedImageForPreview.index] || ''}
                      onChange={(e) => handleDescriptionChange(selectedImageForPreview.area, selectedImageForPreview.index, e.target.value)}
                      placeholder="Add a description for this image..."
                      rows={3}
                      className="description-textarea-modal"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SVG Editor - full editor only, resizable + fullscreen */}
        {isEditorOpen && (
          <div
            className={`modal-overlay svg-editor-overlay ${isEditorFullScreen ? 'svg-editor-overlay--fullscreen' : ''}`}
            onClick={handleCloseEditor}
          >
            <div
              className={`svg-editor-container ${isEditorFullScreen ? 'svg-editor-container--fullscreen' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="svg-editor-toolbar-row">
                <button
                  type="button"
                  className="svg-editor-toolbar-btn svg-editor-save-btn"
                  onClick={() => {
                    if (iframeRef.current?.contentWindow) {
                      iframeRef.current.contentWindow.postMessage({ type: 'trigger-save' }, '*');
                    }
                  }}
                  title="Save drawing"
                  aria-label="Save drawing"
                >
                  <i className="fas fa-save" />
                  <span>Save drawing</span>
                </button>
                <button
                  type="button"
                  className="svg-editor-toolbar-btn"
                  onClick={toggleEditorFullScreen}
                  title={isEditorFullScreen ? 'Exit full screen' : 'Full screen'}
                  aria-label={isEditorFullScreen ? 'Exit full screen' : 'Full screen'}
                >
                  <i className={isEditorFullScreen ? 'fas fa-compress' : 'fas fa-expand'} />
                </button>
                <button
                  type="button"
                  className="svg-editor-toolbar-btn"
                  onClick={handleCloseEditor}
                  title="Close editor"
                  aria-label="Close editor"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
              <iframe
                  ref={iframeRef}
                  src={editorIframeSrc ?? ''}
                  title="SVG Editor"
                  className="editor-iframe"
                  onLoad={() => {
                    console.log('SVG Editor iframe loaded');
                  }}
                />
            </div>
          </div>
        )}

        {/* CAD Viewer Modal */}
        {isCadViewerOpen && (
          <div
            className={`modal-overlay svg-editor-overlay ${isCadViewerFullScreen ? 'svg-editor-overlay--fullscreen' : ''}`}
            onClick={handleCloseCadViewer}
          >
            <div
              className={`svg-editor-container ${isCadViewerFullScreen ? 'svg-editor-container--fullscreen' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="svg-editor-toolbar-row">
                <button
                  type="button"
                  className="svg-editor-toolbar-btn svg-editor-save-btn"
                  onClick={handleRequestSvgExport}
                  title="Export SVG to Analysis"
                  aria-label="Export SVG to Analysis"
                  style={{ marginRight: 'auto' }}
                >
                  <i className="fas fa-file-export" />
                  <span>Export SVG to Analysis</span>
                </button>
                <button
                  type="button"
                  className="svg-editor-toolbar-btn"
                  onClick={toggleCadViewerFullScreen}
                  title={isCadViewerFullScreen ? 'Exit full screen' : 'Full screen'}
                  aria-label={isCadViewerFullScreen ? 'Exit full screen' : 'Full screen'}
                >
                  <i className={isCadViewerFullScreen ? 'fas fa-compress' : 'fas fa-expand'} />
                </button>
                <button
                  type="button"
                  className="svg-editor-toolbar-btn"
                  onClick={handleCloseCadViewer}
                  title="Close CAD Viewer"
                  aria-label="Close CAD Viewer"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
              <iframe
                ref={cadViewerIframeRef}
                src={cadViewerIframeSrc ?? ''}
                title="CAD Viewer"
                className="editor-iframe"
                scrolling="no"
                allow="clipboard-read; clipboard-write; fullscreen"
                allowFullScreen
                onLoad={() => {
                  console.log('CAD Viewer iframe loaded');
                }}
              />
            </div>
          </div>
        )}

        {/* SVG Preview Modal */}
        {isSvgModalOpen && selectedSvgForPreview && (
          <div className="modal-overlay" onClick={handleCloseSvgModal}>
            <div className="modal-content svg-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>SVG Preview: {selectedSvgForPreview.name}</h3>
                <button className="modal-close" onClick={handleCloseSvgModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body svg-modal-body">
                <div className="svg-preview-full">
                  <img 
                    src={selectedSvgForPreview.preview} 
                    alt={selectedSvgForPreview.name}
                    className="full-size-svg"
                  />
                </div>
                <div className="svg-preview-info-modal">
                  <div className="info-item">
                    <strong>File Name:</strong> {selectedSvgForPreview.name}
                  </div>
                  <div className="info-item">
                    <strong>Type:</strong> SVG Drawing
                  </div>
                  <div className="info-item">
                    <strong>Actions:</strong>
                    <div className="svg-modal-actions">
                      <button 
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          // Create a blob from the SVG data
                          const svgBlob = new Blob([selectedSvgForPreview.data], { type: 'image/svg+xml' });
                          const url = URL.createObjectURL(svgBlob);
                          
                          // Create download link
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = selectedSvgForPreview.name;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          
                          // Clean up the blob URL
                          URL.revokeObjectURL(url);
                        }}
                      >
                        <i className="fas fa-download"></i>
                        Download SVG
                      </button>
                      <button 
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          handleEditSvg(selectedSvgForPreview);
                          handleCloseSvgModal();
                        }}
                      >
                        <i className="fas fa-edit"></i>
                        Edit SVG
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Weather verification generator */}
        {isWeatherGenModalOpen && (
          <div className="modal-overlay" onClick={handleCloseWeatherGenModal}>
            <div
              className="modal-content"
              style={{ maxWidth: '640px', maxHeight: '92vh', overflow: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header wv-modal-header">
                <h3>Weather verification report</h3>
                <button type="button" className="modal-close wv-modal-close" onClick={handleCloseWeatherGenModal}>
                  <i className="fas fa-times" />
                </button>
              </div>
              <div className="modal-body" style={{ padding: '20px' }}>
                {weatherGenErr ? (
                  <div className="wv-error">
                    {weatherGenErr}
                  </div>
                ) : null}

                <div className="wv-essentials">
                  <div className="wv-essentials-header">
                    <p className="wv-essentials-title">Property &amp; loss date</p>
                  </div>
                  <div className="wv-essentials-fields">
                    <div className="wv-field">
                      <label className="wv-field-label" htmlFor="wv_address">
                        Property address
                      </label>
                      <div className="wv-address-autocomplete-wrapper" style={{ position: 'relative' }}>
                        <input
                          id="wv_address"
                          type="text"
                          className="wv-field-input"
                          value={weatherGenForm.address}
                          onChange={(e) => {
                            setShowWvAddressSuggestions(true);
                            setWvAddressLookupMessage('');
                            if (!e.target.value.trim()) {
                              setWvAddressSuggestions([]);
                            }
                            setWeatherGenForm((f) => ({ ...f, address: e.target.value }));
                          }}
                          onFocus={() => setShowWvAddressSuggestions(true)}
                          ref={wvAddressInputRef}
                          placeholder="Street, city, state"
                          maxLength={500}
                          autoComplete="street-address"
                          style={{ paddingRight: '4.6rem' }}
                        />
                        <AddressPicker
                          initialAddress={weatherGenForm.address}
                          onSelect={(address) => {
                            setWeatherGenForm((f) => ({ ...f, address }));
                            setShowWvAddressSuggestions(false);
                          }}
                        />
                        {showWvAddressSuggestions && hasPlacesApiKey && (
                          <div className="wv-address-suggestions-dropdown" ref={wvAddressSuggestionsRef}>
                            {isWvAddressLookupLoading && (
                              <div className="address-suggestion-item address-suggestion-state">Searching addresses...</div>
                            )}
                            {!isWvAddressLookupLoading && wvAddressLookupMessage && weatherGenForm.address.trim().length >= 3 && (
                              <div className="address-suggestion-item address-suggestion-state">{wvAddressLookupMessage}</div>
                            )}
                            {!isWvAddressLookupLoading && wvAddressSuggestions.map((suggestion) => (
                              <button
                                type="button"
                                key={suggestion.place_id}
                                className="address-suggestion-item"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleWvAddressSuggestionSelect(suggestion.description);
                                }}
                              >
                                {suggestion.description}
                              </button>
                         ))}
                           </div>
                         )}
                       </div>
                     </div>
                    <div className="wv-field wv-field--date">
                      <label className="wv-field-label" htmlFor="wv_dol">
                        Date of loss
                      </label>
                      <input
                        id="wv_dol"
                        type="date"
                        className="wv-field-input wv-field-input--date"
                        value={weatherGenForm.date_of_loss}
                        onChange={(e) =>
                          setWeatherGenForm((f) => ({ ...f, date_of_loss: e.target.value }))
                        }
                      />
                      {(() => {
                        const lossDateLabel = formatLossDateDisplay(weatherGenForm.date_of_loss);
                        if (lossDateLabel) {
                          return <p className="wv-date-preview">{lossDateLabel}</p>;
                        }
                      })()}
                    </div>
                  </div>
                </div>

                <div className="wv-sections">
                  <div className="wv-sections-header">
                    <div>
                      <p className="wv-sections-title">Report sections</p>
                      <p className="wv-sections-subtitle">
                        Choose what sections to appear in report
                      </p>
                    </div>
                  </div>
                  {WEATHER_REPORT_SECTION_GROUPS.map((group) => (
                    <div key={group.id} className="wv-section-group">
                      <div className="wv-section-group-header">
                        <span className="wv-section-group-title">{group.title}</span>
                        {group.hint ? (
                          <span className="wv-section-group-hint">{group.hint}</span>
                        ) : null}
                      </div>
                      <div className="wv-section-grid">
                        {group.sections.map((section) => {
                          const checked = Boolean(weatherGenForm[section.key]);
                          return (
                            <label
                              key={section.key}
                              className={`wv-section-chip${checked ? ' wv-section-chip--on' : ''}`}
                            >
                              <input
                                type="checkbox"
                                className="wv-section-chip-input"
                                checked={checked}
                                onChange={(e) =>
                                  setWeatherGenForm((f) => ({
                                    ...f,
                                    [section.key]: e.target.checked,
                                  }))
                                }
                              />
                              <span className="wv-section-chip-body">
                                <span className="wv-section-chip-label">{section.label}</span>
                                <span className="wv-section-chip-desc">{section.hint}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <details className="wv-advanced" style={{ marginTop: '8px', marginBottom: '12px' }}>
                  <summary>Advanced</summary>
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label htmlFor="wv_rs">Range start (optional)</label>
                        <input
                          id="wv_rs"
                          type="date"
                          className="form-control"
                          value={weatherGenForm.range_start}
                          onChange={(e) => setWeatherGenForm((f) => ({ ...f, range_start: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="wv_re">Range end (optional)</label>
                        <input
                          id="wv_re"
                          type="date"
                          className="form-control"
                          value={weatherGenForm.range_end}
                          onChange={(e) => setWeatherGenForm((f) => ({ ...f, range_end: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label htmlFor="wv_thr">Wind threshold (mph)</label>
                        <input
                          id="wv_thr"
                          type="number"
                          step="0.1"
                          className="form-control"
                          value={weatherGenForm.threshold_mph}
                          onChange={(e) => setWeatherGenForm((f) => ({ ...f, threshold_mph: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="wv_rain">Rain threshold (mm)</label>
                        <input
                          id="wv_rain"
                          type="number"
                          step="0.1"
                          className="form-control"
                          value={weatherGenForm.rain_mm}
                          onChange={(e) => setWeatherGenForm((f) => ({ ...f, rain_mm: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="form-group" style={{ maxWidth: '220px' }}>
                      <label htmlFor="wv_md">Multi-day window (days)</label>
                      <select
                        id="wv_md"
                        className="form-control"
                        value={weatherGenForm.multi_day_days}
                        onChange={(e) =>
                          setWeatherGenForm((f) => ({
                            ...f,
                            multi_day_days: Number(e.target.value) as WeatherMultiDayDays,
                          }))
                        }
                      >
                        <option value={1}>1</option>
                        <option value={3}>3</option>
                        <option value={5}>5</option>
                      </select>
                    </div>
                  </div>
                </details>

                {weatherGenStats ? (
                  <p className="wv-stats">
                    Stations: <strong>{weatherGenStats.stations}</strong>, ISD:{' '}
                    <strong>{weatherGenStats.isd_stations}</strong>, wind events:{' '}
                    <strong>{weatherGenStats.wind_events}</strong>, storm events:{' '}
                    <strong>{weatherGenStats.storm_events}</strong>
                  </p>
                ) : null}

                {weatherGenResultText ? (
                  <div style={{ marginBottom: '12px' }}>
                    <button
                      type="button"
                      className="btn-secondary wv-preview-toggle"
                      onClick={() => setWeatherGenPreviewOpen((v) => !v)}
                    >
                      {weatherGenPreviewOpen ? 'Hide' : 'Show'} text preview
                    </button>
                    {weatherGenPreviewOpen ? (
                      <pre className="wv-preview">
                        {weatherGenResultText.slice(0, 120000)}
                        {weatherGenResultText.length > 120000 ? '\n… (truncated in preview)' : ''}
                      </pre>
                    ) : null}
                  </div>
                ) : null}

                <div className="wv-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleWeatherGenSubmit}
                    disabled={weatherGenLoading || weatherGenPdfLoading}
                  >
                    {weatherGenLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin" /> Generating…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sync" /> Generate
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleWeatherGenDownloadPdf}
                    disabled={!weatherGenResultText || weatherGenPdfLoading || weatherGenLoading}
                  >
                    {weatherGenPdfLoading ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-file-pdf" />
                    )}{' '}
                    Download PDF
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleWeatherGenAttachPdf}
                    disabled={!weatherGenResultText || weatherGenPdfLoading || weatherGenLoading}
                  >
                    Attach PDF to this case
                  </button>
                  <button type="button" className="btn-secondary" onClick={handleCloseWeatherGenModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Wind Report Extraction Results Modal */}
        {isWindReportModalOpen && windReportExtraction && (
          <div className="modal-overlay" onClick={handleCloseWindReportModal}>
            <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>🌪️ Wind Report Extraction Results</h3>
                <button className="modal-close" onClick={handleCloseWindReportModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body" style={{ padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>
                    <i className="fas fa-file-pdf"></i> {windReportExtraction.source_file}
                  </h4>
                  <p style={{ color: '#666', fontSize: '14px' }}>
                    Text Length: {windReportExtraction.raw_text_length?.toLocaleString()} characters
                  </p>
                </div>

                {windReportExtraction.error ? (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffc107', 
                    borderRadius: '5px',
                    marginBottom: '15px'
                  }}>
                    <strong>⚠️ Extraction Warning:</strong>
                    <p style={{ marginTop: '10px' }}>{windReportExtraction.error}</p>
                    {windReportExtraction.note && (
                      <p style={{ marginTop: '10px', fontStyle: 'italic' }}>{windReportExtraction.note}</p>
                    )}
                  </div>
                ) : null}

                {windReportExtraction.summary && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>
                      <i className="fas fa-list"></i> Summary
                    </h4>
                    <div style={{ 
                      padding: '15px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '5px',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.6'
                    }}>
                      {windReportExtraction.summary}
                    </div>
                  </div>
                )}

                {windReportExtraction.extracted_data && Object.keys(windReportExtraction.extracted_data).length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>
                      <i className="fas fa-database"></i> Structured Data
                    </h4>
                    <div style={{ 
                      padding: '15px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '5px',
                      overflow: 'auto',
                      maxHeight: '400px'
                    }}>
                      <pre style={{ 
                        margin: 0, 
                        fontFamily: 'monospace', 
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {JSON.stringify(windReportExtraction.extracted_data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {windReportExtraction.extracted_data?.wind_speeds && Object.keys(windReportExtraction.extracted_data.wind_speeds).length > 0 && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#e7f3ff', 
                    borderRadius: '5px',
                    marginBottom: '15px'
                  }}>
                    <h5 style={{ marginTop: 0, color: '#0066cc' }}>🌬️ Wind Speeds:</h5>
                    <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                      {windReportExtraction.extracted_data.wind_speeds.max && (
                        <li><strong>Max:</strong> {windReportExtraction.extracted_data.wind_speeds.max}</li>
                      )}
                      {windReportExtraction.extracted_data.wind_speeds.average && (
                        <li><strong>Average:</strong> {windReportExtraction.extracted_data.wind_speeds.average}</li>
                      )}
                      {windReportExtraction.extracted_data.wind_speeds.gusts && (
                        <li><strong>Gusts:</strong> {windReportExtraction.extracted_data.wind_speeds.gusts}</li>
                      )}
                    </ul>
                  </div>
                )}

                {windReportExtraction.extracted_data?.wind_directions && windReportExtraction.extracted_data.wind_directions.length > 0 && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#e7f3ff', 
                    borderRadius: '5px',
                    marginBottom: '15px'
                  }}>
                    <h5 style={{ marginTop: 0, color: '#0066cc' }}>🧭 Wind Directions:</h5>
                    <p>{windReportExtraction.extracted_data.wind_directions.join(', ')}</p>
                  </div>
                )}

                {windReportExtraction.extracted_data?.dates && windReportExtraction.extracted_data.dates.length > 0 && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#e7f3ff', 
                    borderRadius: '5px',
                    marginBottom: '15px'
                  }}>
                    <h5 style={{ marginTop: 0, color: '#0066cc' }}>📅 Dates:</h5>
                    <p>{windReportExtraction.extracted_data.dates.join(', ')}</p>
                  </div>
                )}

                {windReportExtraction.extracted_data?.location && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#e7f3ff', 
                    borderRadius: '5px',
                    marginBottom: '15px'
                  }}>
                    <h5 style={{ marginTop: 0, color: '#0066cc' }}>📍 Location:</h5>
                    <p>{windReportExtraction.extracted_data.location}</p>
                  </div>
                )}

                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleCloseWindReportModal}
                    style={{ width: '100%' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisForm; 